import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Users,
  Gavel,
  FileText,
  Play,
  Pause,
  MessageSquare,
  Clock,
  Award,
  BookOpen,
  UserCircle,
  ArrowRight,
  Plus,
  AlertCircle,
  CheckCircle,
  Volume2,
  Hand,
  Send,
  RotateCcw,
  Crown,
  Shield,
  User,
  Briefcase,
  Target,
  Zap,
  Trophy,
  Star,
  ChevronRight,
  Search,
  Check,
  X,
  Eye,
  AlertTriangle,
  Loader2,
  Swords,
  Flame,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VirtualCourt() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home'); // home, games, prosecutor-game, courtroom, leaderboard, profile
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentPhase, setCurrentPhase] = useState('opening');
  const [timer, setTimer] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // بيانات لعبة وكيل النيابة
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [gameTimer, setGameTimer] = useState(0);
  const [selectedEvidence, setSelectedEvidence] = useState([]);
  const [selectedCulprits, setSelectedCulprits] = useState([]);
  const [accusation, setAccusation] = useState('');
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [legalArticles, setLegalArticles] = useState([]);
  const [gameResult, setGameResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [gameStep, setGameStep] = useState(1); // 1: الأدلة، 2: الجناة، 3: التهمة، 4: المواد

  // بيانات لعبة المرافعة الذهبية
  const [pleadingScenarios, setPleadingScenarios] = useState([]);
  const [selectedDefenses, setSelectedDefenses] = useState([]);
  const [currentGameType, setCurrentGameType] = useState('prosecutor'); // prosecutor, pleading, error

  // بيانات لعبة الخطأ الإجرائي
  const [errorScenarios, setErrorScenarios] = useState([]);
  const [selectedErrors, setSelectedErrors] = useState([]);

  // بيانات لعبة سباق المحكمة (الحركية التنافسية)
  const [raceGameActive, setRaceGameActive] = useState(false);
  const [raceQuestions, setRaceQuestions] = useState([]);
  const [currentRaceQuestion, setCurrentRaceQuestion] = useState(0);
  const [raceScore, setRaceScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [raceTimer, setRaceTimer] = useState(10);
  const [raceAnswered, setRaceAnswered] = useState(false);
  const [raceResult, setRaceResult] = useState(null);
  const [playerPosition, setPlayerPosition] = useState(0);
  const [opponentPosition, setOpponentPosition] = useState(0);
  const [showRaceAnimation, setShowRaceAnimation] = useState(false);

  // التحقق من تسجيل الدخول
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // جلب السيناريوهات عند فتح تبويب الألعاب
  useEffect(() => {
    if (activeTab === 'games' || activeTab === 'prosecutor-game') {
      fetchScenarios();
      fetchLegalArticles();
      fetchPleadingScenarios();
      fetchErrorScenarios();
    }
    if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    }
    if (activeTab === 'profile' && isLoggedIn) {
      fetchUserProfile();
    }
  }, [activeTab, isLoggedIn]);

  // مؤقت اللعبة
  useEffect(() => {
    let interval;
    if (gameInProgress && selectedScenario) {
      interval = setInterval(() => {
        setGameTimer(prev => {
          if (prev >= selectedScenario.time_limit * 60) {
            clearInterval(interval);
            handleSubmitGame();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameInProgress, selectedScenario]);

  const fetchScenarios = async () => {
    try {
      const res = await axios.get(`${API}/api/virtual-court/prosecutor-game/scenarios`);
      setScenarios(res.data.scenarios);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
    }
  };

  const fetchLegalArticles = async () => {
    try {
      const res = await axios.get(`${API}/api/virtual-court/legal-articles`);
      setLegalArticles(res.data.articles);
    } catch (err) {
      console.error('Error fetching articles:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/api/virtual-court/leaderboard`);
      setLeaderboard(res.data.leaderboard);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/virtual-court/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(res.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchPleadingScenarios = async () => {
    try {
      const res = await axios.get(`${API}/api/virtual-court/golden-pleading/scenarios`);
      setPleadingScenarios(res.data.scenarios);
    } catch (err) {
      console.error('Error fetching pleading scenarios:', err);
    }
  };

  const fetchErrorScenarios = async () => {
    try {
      const res = await axios.get(`${API}/api/virtual-court/procedural-error/scenarios`);
      setErrorScenarios(res.data.scenarios);
    } catch (err) {
      console.error('Error fetching error scenarios:', err);
    }
  };

  const startGame = (scenario, gameType = 'prosecutor') => {
    // السماح للجميع ببدء اللعبة والتجربة
    setSelectedScenario(scenario);
    setCurrentGameType(gameType);
    setGameInProgress(true);
    setGameTimer(0);
    setSelectedEvidence([]);
    setSelectedCulprits([]);
    setAccusation('');
    setSelectedArticles([]);
    setSelectedDefenses([]);
    setSelectedErrors([]);
    setGameResult(null);
    setGameStep(1);
    setActiveTab('prosecutor-game');
  };

  const handleSubmitPleadingGame = async () => {
    setLoading(true);
    
    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/api/virtual-court/golden-pleading/submit`, {
          scenario_id: selectedScenario.id,
          selected_defenses: selectedDefenses,
          time_taken: gameTimer
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setGameResult(res.data);
        setGameInProgress(false);
      } catch (err) {
        console.error('Error submitting game:', err);
        alert('حدث خطأ أثناء تقديم الإجابة');
      } finally {
        setLoading(false);
      }
    } else {
      // للزوار
      const maxScore = selectedScenario.points;
      let score = selectedDefenses.length * 15;
      setGameResult({
        score: Math.min(score, maxScore),
        max_score: maxScore,
        passed: score >= maxScore * 0.5,
        feedback: [
          {type: 'partial', message: `اخترت ${selectedDefenses.length} حجة`},
          {type: 'info', message: '⚠️ سجل الدخول لرؤية التقييم الكامل'}
        ],
        correct_answers: null,
        is_guest: true
      });
      setGameInProgress(false);
      setLoading(false);
    }
  };

  const handleSubmitErrorGame = async () => {
    setLoading(true);
    
    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/api/virtual-court/procedural-error/submit`, {
          scenario_id: selectedScenario.id,
          selected_errors: selectedErrors,
          time_taken: gameTimer
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setGameResult(res.data);
        setGameInProgress(false);
      } catch (err) {
        console.error('Error submitting game:', err);
        alert('حدث خطأ أثناء تقديم الإجابة');
      } finally {
        setLoading(false);
      }
    } else {
      // للزوار
      const maxScore = selectedScenario.points;
      let score = selectedErrors.length * 20;
      setGameResult({
        score: Math.min(score, maxScore),
        max_score: maxScore,
        passed: score >= maxScore * 0.5,
        feedback: [
          {type: 'partial', message: `حددت ${selectedErrors.length} خطأ`},
          {type: 'info', message: '⚠️ سجل الدخول لرؤية الإجابات الصحيحة'}
        ],
        correct_answers: null,
        is_guest: true
      });
      setGameInProgress(false);
      setLoading(false);
    }
  };

  const toggleDefense = (defenseId) => {
    setSelectedDefenses(prev =>
      prev.includes(defenseId)
        ? prev.filter(id => id !== defenseId)
        : [...prev, defenseId]
    );
  };

  const toggleError = (errorId) => {
    setSelectedErrors(prev =>
      prev.includes(errorId)
        ? prev.filter(id => id !== errorId)
        : [...prev, errorId]
    );
  };

  // ====== لعبة سباق المحكمة - 100 سؤال في الأحوال الشخصية ======
  const raceQuestionsData = [
    // ===== الزواج والأهلية (1-15) =====
    { id: 1, category: "الزواج والأهلية", question: "ما هو السن الأدنى النظامي لإتمام عقد الزواج؟", options: ["15 سنة", "18 سنة", "21 سنة", "لا يوجد حد"], correct: 1, points: 10, reference: "المادة التاسعة - نظام الأحوال الشخصية" },
    { id: 2, category: "الزواج والأهلية", question: "ما هو الحكم إذا اشترطت الزوجة في عقد النكاح ألا يتزوج عليها؟", options: ["الشرط باطل والعقد صحيح", "الشرط صحيح ولها حق الفسخ", "الشرط صحيح ولها التعويض فقط", "العقد باطل"], correct: 1, points: 15, reference: "المادة الثالثة والعشرون" },
    { id: 3, category: "الزواج والأهلية", question: "من هو الولي في عقد النكاح؟", options: ["الأب فقط", "الأقرب فالأقرب من العصبة", "أي قريب", "القاضي دائماً"], correct: 1, points: 10, reference: "المادة الخامسة عشرة" },
    { id: 4, category: "الزواج والأهلية", question: "ما حكم زواج المحلل؟", options: ["صحيح", "باطل", "موقوف على الإجازة", "صحيح مع الإثم"], correct: 1, points: 10, reference: "أحكام النكاح الباطل" },
    { id: 5, category: "الزواج والأهلية", question: "متى يجوز للمرأة الزواج بدون إذن وليها؟", options: ["إذا بلغت 25 سنة", "إذا عضلها الولي", "لا يجوز مطلقاً", "إذا كانت موظفة"], correct: 1, points: 15, reference: "المادة التاسعة عشرة" },
    { id: 6, category: "الزواج والأهلية", question: "ما هو ركن عقد النكاح الأساسي؟", options: ["الشهود", "الإيجاب والقبول", "المهر", "الولي"], correct: 1, points: 10, reference: "المادة السادسة" },
    { id: 7, category: "الزواج والأهلية", question: "ما الحد الأدنى للمهر في النظام السعودي؟", options: ["لا حد أدنى", "ألف ريال", "خمسة آلاف ريال", "عشرة آلاف ريال"], correct: 0, points: 10, reference: "المادة الثامنة والعشرون" },
    { id: 8, category: "الزواج والأهلية", question: "هل يصح عقد النكاح بدون شهود؟", options: ["نعم", "لا", "نعم إذا أُعلن", "موقوف على الإجازة"], correct: 1, points: 10, reference: "المادة الثانية عشرة" },
    { id: 9, category: "الزواج والأهلية", question: "ما حكم اشتراط الزوجة إكمال دراستها؟", options: ["شرط باطل", "شرط صحيح ملزم", "موقوف على موافقة الزوج", "يبطل العقد"], correct: 1, points: 10, reference: "المادة الثالثة والعشرون" },
    { id: 10, category: "الزواج والأهلية", question: "كم عدد الشهود المطلوبين في عقد النكاح؟", options: ["شاهد واحد", "شاهدان", "ثلاثة شهود", "أربعة شهود"], correct: 1, points: 10, reference: "المادة الثانية عشرة" },
    { id: 11, category: "الزواج والأهلية", question: "ما حكم زواج المتعة في النظام السعودي؟", options: ["جائز", "محرم وباطل", "مكروه", "جائز بشروط"], correct: 1, points: 10, reference: "أحكام النكاح" },
    { id: 12, category: "الزواج والأهلية", question: "من يتولى تزويج من لا ولي لها؟", options: ["أي رجل صالح", "القاضي", "مدير الأحوال المدنية", "لا يمكن تزويجها"], correct: 1, points: 10, reference: "المادة الحادية والعشرون" },
    { id: 13, category: "الزواج والأهلية", question: "ما المقصود بنكاح الشغار؟", options: ["الزواج بدون مهر مقابل تزويج أخرى", "الزواج السري", "الزواج المؤقت", "زواج المسيار"], correct: 0, points: 15, reference: "أحكام النكاح الباطل" },
    { id: 14, category: "الزواج والأهلية", question: "متى يكون عقد النكاح موقوفاً؟", options: ["إذا زوج الفضولي", "إذا كان بدون شهود", "إذا كان بدون مهر", "لا يوجد عقد موقوف"], correct: 0, points: 15, reference: "المادة الرابعة عشرة" },
    { id: 15, category: "الزواج والأهلية", question: "ما أثر الرضا في عقد النكاح؟", options: ["ركن", "شرط صحة", "شرط نفاذ", "مستحب"], correct: 0, points: 10, reference: "المادة السابعة" },
    
    // ===== الحضانة (16-30) =====
    { id: 16, category: "الحضانة", question: "من له الأولوية في حضانة الأبناء عند الفرقة؟", options: ["الأب", "الأم", "الجدة لأم", "الجدة لأب"], correct: 1, points: 10, reference: "المادة الخامسة والعشرون بعد المائة" },
    { id: 17, category: "الحضانة", question: "متى تنتهي حضانة الذكر؟", options: ["7 سنوات", "15 سنة", "18 سنة", "البلوغ"], correct: 2, points: 10, reference: "المادة السادسة والعشرون بعد المائة" },
    { id: 18, category: "الحضانة", question: "متى تنتهي حضانة الأنثى؟", options: ["9 سنوات", "15 سنة", "الزواج أو 18 سنة", "البلوغ"], correct: 2, points: 10, reference: "المادة السادسة والعشرون بعد المائة" },
    { id: 19, category: "الحضانة", question: "هل يسقط حق الأم في الحضانة بالزواج؟", options: ["نعم دائماً", "لا", "نعم إلا إذا رضي الأب", "يسقط مؤقتاً"], correct: 0, points: 15, reference: "المادة الثامنة والعشرون بعد المائة" },
    { id: 20, category: "الحضانة", question: "من يلي الأم في الحضانة؟", options: ["الأب", "الجدة لأم", "الجدة لأب", "الخالة"], correct: 1, points: 10, reference: "المادة الخامسة والعشرون بعد المائة" },
    { id: 21, category: "الحضانة", question: "ما شروط الحاضن في النظام؟", options: ["الإسلام فقط", "البلوغ والعقل والأمانة", "أن يكون أنثى", "القرابة فقط"], correct: 1, points: 10, reference: "المادة السابعة والعشرون بعد المائة" },
    { id: 22, category: "الحضانة", question: "هل يحق للأب رؤية أولاده في حضانة الأم؟", options: ["لا", "نعم حق مكفول", "فقط بإذن الأم", "فقط في المحكمة"], correct: 1, points: 10, reference: "المادة الثلاثون بعد المائة" },
    { id: 23, category: "الحضانة", question: "من ينظر في نزاعات الحضانة؟", options: ["المحكمة العامة", "محكمة الأحوال الشخصية", "مكتب الصلح", "الشرطة"], correct: 1, points: 10, reference: "اختصاص المحاكم" },
    { id: 24, category: "الحضانة", question: "متى يُخيَّر الطفل بين أبويه؟", options: ["7 سنوات", "10 سنوات", "15 سنة", "لا يُخيَّر"], correct: 2, points: 15, reference: "المادة السادسة والعشرون بعد المائة" },
    { id: 25, category: "الحضانة", question: "هل تسقط الحضانة بسفر الحاضنة؟", options: ["نعم دائماً", "لا", "إذا كان السفر للإقامة", "بحسب المسافة"], correct: 2, points: 15, reference: "المادة التاسعة والعشرون بعد المائة" },
    { id: 26, category: "الحضانة", question: "ما حكم نقل المحضون لبلد آخر؟", options: ["جائز مطلقاً", "يحتاج إذن المحكمة", "ممنوع", "يحتاج موافقة الطرف الآخر"], correct: 1, points: 10, reference: "المادة التاسعة والعشرون بعد المائة" },
    { id: 27, category: "الحضانة", question: "من يتحمل أجرة الحضانة؟", options: ["الحاضنة", "الأب", "الأم", "الدولة"], correct: 1, points: 10, reference: "المادة الحادية والثلاثون بعد المائة" },
    { id: 28, category: "الحضانة", question: "متى تسقط الحضانة عن الأم؟", options: ["بالزواج فقط", "بالفسق أو العجز أو الزواج", "بالسفر", "لا تسقط"], correct: 1, points: 15, reference: "المادة الثامنة والعشرون بعد المائة" },
    { id: 29, category: "الحضانة", question: "هل يحق للجدة الحضانة مع وجود الأم؟", options: ["نعم", "لا إلا بسقوط حق الأم", "نعم بموافقة الأم", "بحكم قضائي فقط"], correct: 1, points: 10, reference: "ترتيب الحاضنين" },
    { id: 30, category: "الحضانة", question: "ما معنى حق الزيارة؟", options: ["السكن مع الطفل", "رؤية المحضون في أوقات محددة", "الحضانة المشتركة", "الإنفاق على الطفل"], correct: 1, points: 10, reference: "المادة الثلاثون بعد المائة" },
    
    // ===== النفقة (31-45) =====
    { id: 31, category: "النفقة", question: "متى تسقط نفقة الزوجة في النظام؟", options: ["إذا كانت موظفة", "إذا منعت نفسها بلا عذر", "إذا سافرت", "لا تسقط"], correct: 1, points: 10, reference: "المادة الخامسة والأربعون" },
    { id: 32, category: "النفقة", question: "ما هي أنواع النفقة الواجبة للزوجة؟", options: ["الطعام فقط", "المسكن والملبس والطعام والعلاج", "الطعام والمسكن", "حسب الاتفاق"], correct: 1, points: 10, reference: "المادة الرابعة والأربعون" },
    { id: 33, category: "النفقة", question: "من يستحق النفقة من الأقارب؟", options: ["الوالدان فقط", "الأولاد فقط", "الأصول والفروع", "جميع الأقارب"], correct: 2, points: 15, reference: "المادة الثامنة والأربعون" },
    { id: 34, category: "النفقة", question: "متى تجب نفقة الأولاد على الأم؟", options: ["عند عجز الأب", "دائماً", "لا تجب", "بعد الطلاق فقط"], correct: 0, points: 15, reference: "المادة التاسعة والأربعون" },
    { id: 35, category: "النفقة", question: "هل نفقة المطلقة رجعياً واجبة؟", options: ["نعم كالزوجة", "لا", "نصف النفقة", "السكن فقط"], correct: 0, points: 10, reference: "المادة الخمسون" },
    { id: 36, category: "النفقة", question: "متى تسقط نفقة البنت؟", options: ["بالبلوغ", "بالزواج أو الكسب", "18 سنة", "21 سنة"], correct: 1, points: 10, reference: "المادة الحادية والخمسون" },
    { id: 37, category: "النفقة", question: "من يقدر النفقة عند الاختلاف؟", options: ["الزوج", "الزوجة", "القاضي", "العرف"], correct: 2, points: 10, reference: "المادة السادسة والأربعون" },
    { id: 38, category: "النفقة", question: "هل تسقط النفقة بالتقادم؟", options: ["نعم بعد 5 سنوات", "لا تسقط", "نعم بعد سنة", "بحكم القاضي"], correct: 1, points: 15, reference: "أحكام النفقة" },
    { id: 39, category: "النفقة", question: "ما حكم نفقة الزوجة الناشز؟", options: ["واجبة", "ساقطة", "نصفها", "السكن فقط"], correct: 1, points: 10, reference: "المادة الخامسة والأربعون" },
    { id: 40, category: "النفقة", question: "هل يجوز الحبس في دين النفقة؟", options: ["لا", "نعم", "للزوجة فقط", "للأولاد فقط"], correct: 1, points: 10, reference: "أحكام التنفيذ" },
    { id: 41, category: "النفقة", question: "من يتحمل نفقة الرضاع؟", options: ["الأم", "الأب", "مناصفة", "الحاضنة"], correct: 1, points: 10, reference: "المادة الثانية والخمسون" },
    { id: 42, category: "النفقة", question: "متى تجب نفقة الوالدين على الولد؟", options: ["دائماً", "عند فقرهما", "عند كبرهما", "لا تجب"], correct: 1, points: 10, reference: "المادة الثامنة والأربعون" },
    { id: 43, category: "النفقة", question: "ما معيار تقدير النفقة؟", options: ["حال الزوج فقط", "حال الزوجة فقط", "حال الزوجين معاً", "الحد الأدنى للمعيشة"], correct: 2, points: 15, reference: "المادة السادسة والأربعون" },
    { id: 44, category: "النفقة", question: "هل تشمل النفقة أجرة الخادمة؟", options: ["لا", "نعم إذا كانت محتاجة", "دائماً", "بحسب العقد"], correct: 1, points: 10, reference: "المادة الرابعة والأربعون" },
    { id: 45, category: "النفقة", question: "متى تستحق المعتدة النفقة؟", options: ["في عدة الطلاق الرجعي فقط", "في جميع أنواع العدة", "لا تستحق", "في عدة الوفاة"], correct: 0, points: 15, reference: "المادة الخمسون" },
    
    // ===== الطلاق (46-60) =====
    { id: 46, category: "الطلاق", question: "ما هي المهلة القصوى لتوثيق الطلاق؟", options: ["15 يوماً", "30 يوماً", "60 يوماً", "90 يوماً"], correct: 1, points: 10, reference: "المادة التسعون" },
    { id: 47, category: "الطلاق", question: "ما حكم الطلاق في الحيض؟", options: ["باطل", "صحيح بدعي", "صحيح سني", "موقوف"], correct: 1, points: 15, reference: "المادة الثمانون" },
    { id: 48, category: "الطلاق", question: "كم عدد الطلقات التي يملكها الزوج؟", options: ["واحدة", "اثنتان", "ثلاث", "غير محدود"], correct: 2, points: 10, reference: "المادة الحادية والثمانون" },
    { id: 49, category: "الطلاق", question: "ما حكم طلاق الهازل؟", options: ["باطل", "صحيح", "موقوف", "قابل للإبطال"], correct: 1, points: 15, reference: "أحكام الطلاق" },
    { id: 50, category: "الطلاق", question: "ما حكم الطلاق المعلق على شرط؟", options: ["باطل", "صحيح إذا تحقق الشرط", "صحيح مطلقاً", "موقوف"], correct: 1, points: 10, reference: "المادة الثانية والثمانون" },
    { id: 51, category: "الطلاق", question: "ما الفرق بين الطلاق الرجعي والبائن؟", options: ["لا فرق", "الرجعي يمكن الرجوع فيه في العدة", "البائن في العدة فقط", "الرجعي بعوض"], correct: 1, points: 10, reference: "المادة الرابعة والثمانون" },
    { id: 52, category: "الطلاق", question: "ما هي عدة المطلقة غير الحامل؟", options: ["شهر", "شهران", "ثلاث حيضات أو 3 أشهر", "4 أشهر و10 أيام"], correct: 2, points: 10, reference: "المادة الخامسة والتسعون" },
    { id: 53, category: "الطلاق", question: "متى تنتهي عدة الحامل؟", options: ["3 أشهر", "بوضع الحمل", "4 أشهر و10 أيام", "سنة"], correct: 1, points: 10, reference: "المادة السادسة والتسعون" },
    { id: 54, category: "الطلاق", question: "ما المقصود بالخلع؟", options: ["طلاق بعوض من الزوجة", "طلاق بدون عوض", "فسخ النكاح", "الطلاق القضائي"], correct: 0, points: 10, reference: "المادة الثامنة والثمانون" },
    { id: 55, category: "الطلاق", question: "هل يقع الطلاق بالكتابة؟", options: ["لا", "نعم", "بشرط النية", "بشرط الشهود"], correct: 1, points: 10, reference: "المادة التاسعة والسبعون" },
    { id: 56, category: "الطلاق", question: "ما حكم طلاق المكره؟", options: ["صحيح", "باطل", "موقوف", "قابل للإبطال"], correct: 1, points: 15, reference: "المادة الثامنة والسبعون" },
    { id: 57, category: "الطلاق", question: "متى يكون الطلاق بائناً بينونة كبرى؟", options: ["الطلقة الأولى", "الطلقة الثالثة", "الخلع", "طلاق قبل الدخول"], correct: 1, points: 10, reference: "المادة الخامسة والثمانون" },
    { id: 58, category: "الطلاق", question: "ما حق المطلقة في المتعة؟", options: ["لا حق لها", "واجبة للجميع", "للمطلقة قبل الدخول", "مستحبة"], correct: 2, points: 15, reference: "المادة الواحدة والتسعون" },
    { id: 59, category: "الطلاق", question: "هل يملك القاضي التطليق لعدم الإنفاق؟", options: ["لا", "نعم", "بعد سنة", "بموافقة الزوج"], correct: 1, points: 10, reference: "المادة السابعة والثمانون" },
    { id: 60, category: "الطلاق", question: "ما عدة المتوفى عنها زوجها؟", options: ["3 أشهر", "3 حيضات", "4 أشهر و10 أيام", "سنة"], correct: 2, points: 10, reference: "المادة السابعة والتسعون" },
    
    // ===== الميراث (61-70) =====
    { id: 61, category: "الميراث", question: "ما نصيب الزوج عند وجود فرع وارث للزوجة؟", options: ["النصف", "الربع", "الثمن", "الثلث"], correct: 1, points: 10, reference: "أحكام المواريث" },
    { id: 62, category: "الميراث", question: "ما نصيب الزوجة عند عدم وجود فرع وارث؟", options: ["النصف", "الربع", "الثمن", "الثلث"], correct: 1, points: 10, reference: "أحكام المواريث" },
    { id: 63, category: "الميراث", question: "متى ترث البنت النصف؟", options: ["دائماً", "إذا انفردت", "مع الابن", "لا ترث النصف"], correct: 1, points: 10, reference: "أحكام المواريث" },
    { id: 64, category: "الميراث", question: "ما نصيب الأم مع الفرع الوارث؟", options: ["الثلث", "السدس", "الربع", "الثمن"], correct: 1, points: 10, reference: "أحكام المواريث" },
    { id: 65, category: "الميراث", question: "من يحجب الإخوة من الميراث؟", options: ["الأم", "الأب والابن", "الزوج", "الجد فقط"], correct: 1, points: 15, reference: "أحكام الحجب" },
    { id: 66, category: "الميراث", question: "ما معنى العول في الميراث؟", options: ["زيادة السهام عن التركة", "نقص السهام", "الحرمان", "الحجب"], correct: 0, points: 15, reference: "مسائل العول" },
    { id: 67, category: "الميراث", question: "هل يرث القاتل من مقتوله؟", options: ["نعم", "لا", "نصف النصيب", "بحكم القاضي"], correct: 1, points: 10, reference: "موانع الإرث" },
    { id: 68, category: "الميراث", question: "ما نصيب الجدة في الميراث؟", options: ["الثلث", "السدس", "الربع", "لا ترث"], correct: 1, points: 10, reference: "أحكام المواريث" },
    { id: 69, category: "الميراث", question: "من هم أصحاب الفروض؟", options: ["الذكور فقط", "من لهم نصيب مقدر", "الإناث فقط", "الأقارب كلهم"], correct: 1, points: 10, reference: "تقسيم الورثة" },
    { id: 70, category: "الميراث", question: "ما المقصود بالعصبة؟", options: ["أقارب الأم", "من يرث بلا تقدير", "الإناث فقط", "أصحاب الفروض"], correct: 1, points: 10, reference: "الإرث بالتعصيب" },
    
    // ===== الوصية (71-80) =====
    { id: 71, category: "الوصية", question: "ما هي النسبة التي لا تتوقف على إجازة الورثة؟", options: ["النصف", "الثلث", "الربع", "الخمس"], correct: 1, points: 10, reference: "المادة الخامسة والسبعون بعد المائة" },
    { id: 72, category: "الوصية", question: "هل تصح الوصية للوارث؟", options: ["نعم مطلقاً", "لا", "بإجازة الورثة", "للزوجة فقط"], correct: 2, points: 15, reference: "المادة الثامنة والسبعون بعد المائة" },
    { id: 73, category: "الوصية", question: "متى تنفذ الوصية؟", options: ["فوراً", "بعد الوفاة", "بعد سنة", "بقرار المحكمة"], correct: 1, points: 10, reference: "أحكام الوصية" },
    { id: 74, category: "الوصية", question: "هل يجوز الرجوع في الوصية؟", options: ["لا", "نعم قبل الوفاة", "بموافقة الموصى له", "بإذن المحكمة"], correct: 1, points: 10, reference: "المادة الثمانون بعد المائة" },
    { id: 75, category: "الوصية", question: "ما حكم الوصية بأكثر من الثلث؟", options: ["باطلة", "صحيحة بإجازة الورثة", "صحيحة مطلقاً", "موقوفة"], correct: 1, points: 10, reference: "المادة السادسة والسبعون بعد المائة" },
    { id: 76, category: "الوصية", question: "هل تصح الوصية الشفوية؟", options: ["لا", "نعم", "بشهادة شاهدين", "للأقارب فقط"], correct: 1, points: 10, reference: "شكل الوصية" },
    { id: 77, category: "الوصية", question: "ما المقصود بالوصية الواجبة؟", options: ["وصية لغير الوارث", "وصية لأولاد الابن المتوفى", "وصية بالثلث", "لا توجد وصية واجبة"], correct: 1, points: 15, reference: "الوصية الواجبة" },
    { id: 78, category: "الوصية", question: "من يتولى تنفيذ الوصية؟", options: ["الورثة", "الوصي المعين", "المحكمة", "الدولة"], correct: 1, points: 10, reference: "تنفيذ الوصية" },
    { id: 79, category: "الوصية", question: "هل تصح الوصية لجهة خيرية؟", options: ["لا", "نعم", "بإذن الورثة", "بحد الثلث فقط"], correct: 1, points: 10, reference: "المادة الخامسة والسبعون بعد المائة" },
    { id: 80, category: "الوصية", question: "متى تبطل الوصية؟", options: ["بوفاة الموصي", "بوفاة الموصى له قبل الموصي", "بمرور سنة", "لا تبطل"], correct: 1, points: 15, reference: "بطلان الوصية" },
    
    // ===== العضل (81-85) =====
    { id: 81, category: "العضل", question: "ما المقصود بالعضل؟", options: ["منع المرأة من الزواج بكفء", "الطلاق", "الخلع", "فسخ النكاح"], correct: 0, points: 10, reference: "المادة التاسعة عشرة" },
    { id: 82, category: "العضل", question: "ما حكم عضل المرأة؟", options: ["جائز", "محرم", "مكروه", "مباح"], correct: 1, points: 10, reference: "أحكام العضل" },
    { id: 83, category: "العضل", question: "ماذا تفعل المرأة إذا عضلها وليها؟", options: ["لا شيء", "ترفع للقاضي", "تتزوج بدون إذن", "تنتظر"], correct: 1, points: 10, reference: "المادة التاسعة عشرة" },
    { id: 84, category: "العضل", question: "من ينتقل إليه حق الولاية عند العضل؟", options: ["الولي الأبعد", "القاضي", "أي رجل صالح", "تسقط الولاية"], correct: 1, points: 15, reference: "المادة الحادية والعشرون" },
    { id: 85, category: "العضل", question: "هل يُعاقب الولي العاضل؟", options: ["لا", "نعم بالتعزير", "بالسجن فقط", "بالغرامة فقط"], correct: 1, points: 15, reference: "عقوبات العضل" },
    
    // ===== الرضاع (86-90) =====
    { id: 86, category: "الرضاع", question: "كم عدد الرضعات المحرِّمة؟", options: ["3 رضعات", "5 رضعات", "7 رضعات", "10 رضعات"], correct: 1, points: 10, reference: "أحكام الرضاع" },
    { id: 87, category: "الرضاع", question: "ما الذي يثبت بالرضاع؟", options: ["النسب", "التحريم فقط", "النفقة", "الميراث"], correct: 1, points: 10, reference: "أحكام الرضاع" },
    { id: 88, category: "الرضاع", question: "متى يكون الرضاع محرِّماً؟", options: ["في أي سن", "في الحولين", "قبل الفطام", "في السنة الأولى"], correct: 1, points: 10, reference: "شروط الرضاع المحرم" },
    { id: 89, category: "الرضاع", question: "هل يحرم لبن الميتة؟", options: ["لا", "نعم", "بحسب كميته", "بحسب سن الطفل"], correct: 1, points: 15, reference: "أحكام الرضاع" },
    { id: 90, category: "الرضاع", question: "ما حكم زواج أخ من الرضاعة؟", options: ["جائز", "محرم", "مكروه", "بإذن الوالدين"], correct: 1, points: 10, reference: "المحرمات بالرضاع" },
    
    // ===== اللعان (91-93) =====
    { id: 91, category: "اللعان", question: "ما المقصود باللعان؟", options: ["الطلاق", "شهادات مؤكدة بالأيمان لنفي النسب أو القذف", "الخلع", "الظهار"], correct: 1, points: 15, reference: "أحكام اللعان" },
    { id: 92, category: "اللعان", question: "ما أثر اللعان على النكاح؟", options: ["لا أثر", "فسخ مؤبد", "طلاق رجعي", "طلاق بائن"], correct: 1, points: 15, reference: "آثار اللعان" },
    { id: 93, category: "اللعان", question: "هل ينتفي النسب باللعان؟", options: ["لا", "نعم", "بحكم القاضي", "إذا لم تلاعن الزوجة"], correct: 1, points: 10, reference: "أحكام اللعان" },
    
    // ===== مجهول النسب والمفقود (94-97) =====
    { id: 94, category: "مجهول النسب", question: "كيف يثبت نسب مجهول النسب؟", options: ["بالإقرار أو البينة", "بالإقرار فقط", "بالفراش فقط", "لا يثبت"], correct: 0, points: 10, reference: "إثبات النسب" },
    { id: 95, category: "مجهول النسب", question: "من يتولى رعاية مجهول النسب؟", options: ["الدولة", "من وجده", "المحكمة", "أي متبرع"], correct: 0, points: 10, reference: "رعاية مجهولي النسب" },
    { id: 96, category: "المفقود", question: "متى يُحكم بموت المفقود؟", options: ["بعد سنة", "بعد 4 سنوات أو حسب الحال", "بعد 10 سنوات", "فوراً"], correct: 1, points: 15, reference: "أحكام المفقود" },
    { id: 97, category: "المفقود", question: "ما حكم زوجة المفقود؟", options: ["تبقى في عصمته", "تعتد بعد الحكم بموته", "تتزوج فوراً", "ترفع أمرها للقاضي"], correct: 1, points: 10, reference: "المادة المائة" },
    
    // ===== الولاية المالية والكد والسعاية (98-100) =====
    { id: 98, category: "الولاية المالية", question: "من له الولاية المالية على القاصر؟", options: ["الأم", "الأب ثم وصيه ثم القاضي", "الجد فقط", "أي قريب"], correct: 1, points: 10, reference: "الولاية على المال" },
    { id: 99, category: "الكد والسعاية", question: "ما المقصود بالكد والسعاية؟", options: ["النفقة", "حق الزوجة في مال زوجها مقابل عملها", "الميراث", "المهر"], correct: 1, points: 15, reference: "أحكام الكد والسعاية" },
    { id: 100, category: "الولاية المالية", question: "متى تنتهي الولاية المالية؟", options: ["بالبلوغ فقط", "بالبلوغ والرشد", "بالزواج", "بأمر المحكمة"], correct: 1, points: 10, reference: "انتهاء الولاية" }
  ];

  // ====== أسئلة نظام الإجراءات الجزائية - 100 سؤال ======
  const criminalProcedureQuestions = [
    // ===== الدعوى الجزائية (1-15) =====
    { id: 1, category: "الدعوى الجزائية", question: "من يختص بإقامة الدعوى الجزائية العامة؟", options: ["المجني عليه", "النيابة العامة", "الشرطة", "القاضي"], correct: 1, points: 10, reference: "المادة 16 - نظام الإجراءات الجزائية" },
    { id: 2, category: "الدعوى الجزائية", question: "متى تنقضي الدعوى الجزائية العامة؟", options: ["بالتقادم فقط", "بوفاة المتهم أو العفو أو التوبة", "بالصلح فقط", "لا تنقضي"], correct: 1, points: 15, reference: "المادة 22" },
    { id: 3, category: "الدعوى الجزائية", question: "هل يجوز رفع الدعوى الجزائية الخاصة مباشرة للمحكمة؟", options: ["لا مطلقاً", "نعم في جرائم الحق الخاص", "بإذن النيابة فقط", "في الجنايات فقط"], correct: 1, points: 10, reference: "المادة 17" },
    { id: 4, category: "الدعوى الجزائية", question: "ما أثر وفاة المتهم على الدعوى الجزائية؟", options: ["لا أثر", "انقضاء الدعوى العامة", "توقف الدعوى", "انتقالها للورثة"], correct: 1, points: 10, reference: "المادة 22" },
    { id: 5, category: "الدعوى الجزائية", question: "من له حق التنازل عن الدعوى الجزائية الخاصة؟", options: ["النيابة", "المجني عليه أو ورثته", "القاضي", "الشرطة"], correct: 1, points: 10, reference: "المادة 23" },
    { id: 6, category: "الدعوى الجزائية", question: "ما أثر التنازل عن الشكوى؟", options: ["لا أثر", "انقضاء الدعوى الخاصة", "وقف الدعوى مؤقتاً", "تحويلها لدعوى عامة"], correct: 1, points: 10, reference: "المادة 23" },
    { id: 7, category: "الدعوى الجزائية", question: "هل يمتد أثر التنازل لبقية المتهمين؟", options: ["لا", "نعم", "بموافقة النيابة", "بقرار القاضي"], correct: 1, points: 15, reference: "المادة 23" },
    { id: 8, category: "الدعوى الجزائية", question: "متى يجوز للمحكمة نظر الدعوى المدنية التابعة؟", options: ["دائماً", "إذا رفعت مع الدعوى الجزائية", "بطلب النيابة", "لا يجوز"], correct: 1, points: 10, reference: "المادة 24" },
    { id: 9, category: "الدعوى الجزائية", question: "ما الفرق بين الدعوى الجزائية العامة والخاصة؟", options: ["لا فرق", "العامة حق للمجتمع والخاصة حق للمجني عليه", "العامة للجنايات فقط", "الخاصة للمخالفات"], correct: 1, points: 10, reference: "المادتان 16 و17" },
    { id: 10, category: "الدعوى الجزائية", question: "هل يوقف الفصل في الدعوى المدنية الدعوى الجزائية؟", options: ["نعم", "لا", "في الجنايات فقط", "بقرار القاضي"], correct: 1, points: 15, reference: "المادة 25" },
    { id: 11, category: "الدعوى الجزائية", question: "من يمثل الدولة في الدعوى الجزائية؟", options: ["المحامي العام", "النيابة العامة", "وزير العدل", "الشرطة"], correct: 1, points: 10, reference: "المادة 16" },
    { id: 12, category: "الدعوى الجزائية", question: "ما المقصود بالشكوى في النظام الجزائي؟", options: ["الدعوى العامة", "طلب المجني عليه تحريك الدعوى", "البلاغ", "الاستئناف"], correct: 1, points: 10, reference: "المادة 17" },
    { id: 13, category: "الدعوى الجزائية", question: "هل يجوز للنيابة التنازل عن الدعوى العامة؟", options: ["نعم مطلقاً", "لا", "بموافقة المجني عليه", "في المخالفات فقط"], correct: 1, points: 10, reference: "أحكام الدعوى الجزائية" },
    { id: 14, category: "الدعوى الجزائية", question: "متى تُحرَّك الدعوى الجزائية في جرائم العرض؟", options: ["تلقائياً", "بشكوى المجني عليه", "بطلب الشرطة", "بأمر القاضي"], correct: 1, points: 15, reference: "المادة 17" },
    { id: 15, category: "الدعوى الجزائية", question: "ما مصير الدعوى المدنية بعد الحكم بالبراءة؟", options: ["تسقط", "تستمر أمام المحكمة المدنية", "تتحول لتعويض", "تنتهي"], correct: 1, points: 10, reference: "المادة 25" },

    // ===== الضبط الجنائي (16-30) =====
    { id: 16, category: "الضبط الجنائي", question: "من هم رجال الضبط الجنائي؟", options: ["الشرطة فقط", "المحددون بالنظام واللائحة", "أي موظف حكومي", "النيابة العامة"], correct: 1, points: 10, reference: "المادة 26" },
    { id: 17, category: "الضبط الجنائي", question: "ما اختصاص رجال الضبط الجنائي؟", options: ["التحقيق", "البحث عن الجرائم وجمع الاستدلالات", "الحكم", "الادعاء"], correct: 1, points: 10, reference: "المادة 27" },
    { id: 18, category: "الضبط الجنائي", question: "هل يجوز لرجل الضبط القبض على المتهم؟", options: ["نعم مطلقاً", "في حالة التلبس أو بأمر", "لا مطلقاً", "بإذن المتهم"], correct: 1, points: 15, reference: "المادة 33" },
    { id: 19, category: "الضبط الجنائي", question: "ما المقصود بحالة التلبس؟", options: ["الاعتراف", "مشاهدة الجريمة وقت وقوعها أو بعدها بفترة قصيرة", "وجود أدلة", "البلاغ"], correct: 1, points: 10, reference: "المادة 30" },
    { id: 20, category: "الضبط الجنائي", question: "ما واجب رجل الضبط عند تلقي البلاغ؟", options: ["الانتظار", "الانتقال فوراً والمعاينة", "إبلاغ النيابة فقط", "القبض على المشتبه به"], correct: 1, points: 10, reference: "المادة 28" },
    { id: 21, category: "الضبط الجنائي", question: "هل يجوز تفتيش المنزل بدون إذن؟", options: ["نعم دائماً", "في حالة التلبس بجناية", "لا مطلقاً", "بموافقة الجيران"], correct: 1, points: 15, reference: "المادة 41" },
    { id: 22, category: "الضبط الجنائي", question: "من يصدر أمر التفتيش؟", options: ["رجل الضبط", "النيابة العامة أو المحقق", "القاضي فقط", "الشرطة"], correct: 1, points: 10, reference: "المادة 40" },
    { id: 23, category: "الضبط الجنائي", question: "ما مدة التوقيف الأولى من رجل الضبط؟", options: ["12 ساعة", "24 ساعة", "48 ساعة", "72 ساعة"], correct: 1, points: 10, reference: "المادة 35" },
    { id: 24, category: "الضبط الجنائي", question: "أين يُحتجز المقبوض عليه؟", options: ["أي مكان", "دار التوقيف المخصصة", "مركز الشرطة", "المحكمة"], correct: 1, points: 10, reference: "المادة 36" },
    { id: 25, category: "الضبط الجنائي", question: "هل يجوز تفتيش الأنثى بواسطة رجل؟", options: ["نعم", "لا، تفتشها أنثى", "بحضور محرمها", "في الضرورة"], correct: 1, points: 10, reference: "المادة 42" },
    { id: 26, category: "الضبط الجنائي", question: "ما حكم الدليل المتحصل من تفتيش باطل؟", options: ["صحيح", "باطل", "يقدره القاضي", "صحيح جزئياً"], correct: 1, points: 15, reference: "بطلان الإجراءات" },
    { id: 27, category: "الضبط الجنائي", question: "هل يجوز ضبط المراسلات البريدية؟", options: ["نعم مطلقاً", "بإذن من المحقق", "لا مطلقاً", "بموافقة المرسل"], correct: 1, points: 10, reference: "المادة 56" },
    { id: 28, category: "الضبط الجنائي", question: "ما واجب رجل الضبط تجاه المقبوض عليه؟", options: ["حبسه فوراً", "إبلاغه بأسباب القبض وإحالته للنيابة", "التحقيق معه", "إطلاق سراحه"], correct: 1, points: 10, reference: "المادة 35" },
    { id: 29, category: "الضبط الجنائي", question: "هل يجوز دخول المنزل ليلاً للتفتيش؟", options: ["نعم مطلقاً", "لا إلا في حالات الضرورة", "بإذن الجيران", "في الجنايات فقط"], correct: 1, points: 15, reference: "المادة 43" },
    { id: 30, category: "الضبط الجنائي", question: "من يخضع لإشراف النيابة العامة؟", options: ["القضاة", "رجال الضبط الجنائي", "المحامون", "الوزراء"], correct: 1, points: 10, reference: "المادة 26" },

    // ===== التحقيق (31-50) =====
    { id: 31, category: "التحقيق", question: "من يتولى التحقيق في الجرائم؟", options: ["الشرطة", "النيابة العامة", "القاضي", "المحامي"], correct: 1, points: 10, reference: "المادة 14" },
    { id: 32, category: "التحقيق", question: "هل يجوز حضور المحامي مع المتهم أثناء التحقيق؟", options: ["لا", "نعم", "بإذن المحقق", "في الجنايات فقط"], correct: 1, points: 10, reference: "المادة 4" },
    { id: 33, category: "التحقيق", question: "ما مدة التوقيف الأولى من المحقق؟", options: ["24 ساعة", "5 أيام", "10 أيام", "شهر"], correct: 1, points: 10, reference: "المادة 114" },
    { id: 34, category: "التحقيق", question: "من يملك تمديد التوقيف بعد المحقق؟", options: ["رجل الضبط", "رئيس دائرة التحقيق", "القاضي فقط", "المحامي"], correct: 1, points: 15, reference: "المادة 114" },
    { id: 35, category: "التحقيق", question: "ما الحد الأقصى للتوقيف أثناء التحقيق؟", options: ["شهر", "3 أشهر", "6 أشهر", "سنة"], correct: 2, points: 15, reference: "المادة 114" },
    { id: 36, category: "التحقيق", question: "هل يجب تدوين محاضر التحقيق؟", options: ["لا", "نعم", "في الجنايات فقط", "حسب رغبة المحقق"], correct: 1, points: 10, reference: "المادة 95" },
    { id: 37, category: "التحقيق", question: "متى يجوز للمحقق منع المتهم من الكلام؟", options: ["دائماً", "لا يجوز", "في الجرائم الكبرى", "بإذن القاضي"], correct: 1, points: 10, reference: "حقوق المتهم" },
    { id: 38, category: "التحقيق", question: "ما حكم الإكراه على الاعتراف؟", options: ["جائز", "محرم والاعتراف باطل", "جائز في الجنايات", "يقدره القاضي"], correct: 1, points: 10, reference: "المادة 102" },
    { id: 39, category: "التحقيق", question: "هل يجوز الاستعانة بمترجم في التحقيق؟", options: ["لا", "نعم إذا لزم", "بموافقة المتهم", "في القضايا الدولية"], correct: 1, points: 10, reference: "المادة 96" },
    { id: 40, category: "التحقيق", question: "ما المقصود بالندب للتحقيق؟", options: ["تأجيل التحقيق", "تكليف جهة أخرى بإجراء معين", "إنهاء التحقيق", "رفض التحقيق"], correct: 1, points: 10, reference: "المادة 65" },
    { id: 41, category: "التحقيق", question: "هل يجوز للمتهم الصمت أثناء التحقيق؟", options: ["لا", "نعم من حقوقه", "يعتبر اعترافاً", "بإذن المحقق"], correct: 1, points: 10, reference: "المادة 102" },
    { id: 42, category: "التحقيق", question: "متى ينتهي التحقيق؟", options: ["بالاعتراف", "باكتمال الأدلة أو حفظ القضية", "بمرور شهر", "بطلب المتهم"], correct: 1, points: 10, reference: "المادة 124" },
    { id: 43, category: "التحقيق", question: "ما المقصود بأمر حفظ التحقيق؟", options: ["استمرار التحقيق", "عدم إحالة القضية للمحكمة", "إطلاق المتهم", "رد الدعوى"], correct: 1, points: 10, reference: "المادة 124" },
    { id: 44, category: "التحقيق", question: "هل يجوز إعادة فتح التحقيق بعد حفظه؟", options: ["لا مطلقاً", "نعم إذا ظهرت أدلة جديدة", "بطلب المتهم", "بعد سنة"], correct: 1, points: 15, reference: "المادة 125" },
    { id: 45, category: "التحقيق", question: "ما حق المتهم في الاطلاع على الأوراق؟", options: ["لا يحق له", "يحق له ولمحاميه", "بإذن المحقق", "بعد المحاكمة"], correct: 1, points: 10, reference: "المادة 101" },
    { id: 46, category: "التحقيق", question: "هل يجوز مواجهة المتهم بغيره؟", options: ["لا", "نعم إذا رأى المحقق ذلك", "بموافقة المتهم", "في الجنايات فقط"], correct: 1, points: 10, reference: "المادة 100" },
    { id: 47, category: "التحقيق", question: "ما المقصود بالانتقال للمعاينة؟", options: ["السفر للخارج", "ذهاب المحقق لمكان الجريمة", "نقل المتهم", "زيارة السجن"], correct: 1, points: 10, reference: "المادة 79" },
    { id: 48, category: "التحقيق", question: "هل يجوز التحقيق مع الحدث بدون ولي؟", options: ["نعم", "لا يجب حضور ولي أو محامٍ", "في المخالفات فقط", "بإذن القاضي"], correct: 1, points: 15, reference: "نظام الأحداث" },
    { id: 49, category: "التحقيق", question: "ما أثر بطلان إجراء من إجراءات التحقيق؟", options: ["بطلان كل الإجراءات", "بطلانه وما بني عليه", "لا أثر", "إعادة التحقيق"], correct: 1, points: 15, reference: "المادة 191" },
    { id: 50, category: "التحقيق", question: "من يصدر أمر الإحالة للمحكمة؟", options: ["المحقق", "رئيس النيابة", "القاضي", "الشرطة"], correct: 1, points: 10, reference: "المادة 126" },

    // ===== التوقيف والحبس الاحتياطي (51-60) =====
    { id: 51, category: "التوقيف", question: "ما الفرق بين التوقيف والسجن؟", options: ["لا فرق", "التوقيف احتياطي والسجن عقوبة", "التوقيف أطول", "السجن مؤقت"], correct: 1, points: 10, reference: "تعريفات النظام" },
    { id: 52, category: "التوقيف", question: "متى يجوز توقيف المتهم؟", options: ["دائماً", "إذا توافرت دلائل كافية أو خيف هروبه", "بطلبه", "في الجنايات فقط"], correct: 1, points: 10, reference: "المادة 113" },
    { id: 53, category: "التوقيف", question: "هل يجوز الإفراج عن الموقوف بكفالة؟", options: ["لا مطلقاً", "نعم في غير الجرائم الكبرى", "بموافقة المجني عليه", "بعد شهر"], correct: 1, points: 15, reference: "المادة 120" },
    { id: 54, category: "التوقيف", question: "من يملك الإفراج عن الموقوف؟", options: ["رجل الضبط", "المحقق أو من هو أعلى", "القاضي فقط", "المحامي"], correct: 1, points: 10, reference: "المادة 120" },
    { id: 55, category: "التوقيف", question: "ما حق الموقوف في الزيارة؟", options: ["لا يحق له", "يحق له وفق الضوابط", "بإذن المحقق فقط", "بعد شهر"], correct: 1, points: 10, reference: "حقوق الموقوف" },
    { id: 56, category: "التوقيف", question: "هل تُحسب مدة التوقيف من العقوبة؟", options: ["لا", "نعم", "نصفها", "بقرار القاضي"], correct: 1, points: 10, reference: "المادة 223" },
    { id: 57, category: "التوقيف", question: "ما المقصود بالإفراج المؤقت؟", options: ["البراءة", "إطلاق الموقوف مع بقاء الدعوى", "إنهاء التوقيف", "العفو"], correct: 1, points: 10, reference: "المادة 120" },
    { id: 58, category: "التوقيف", question: "هل يجوز توقيف المتهم في المخالفات؟", options: ["نعم دائماً", "لا كقاعدة عامة", "بإذن القاضي", "شهر كحد أقصى"], correct: 1, points: 15, reference: "المادة 113" },
    { id: 59, category: "التوقيف", question: "ما الجهة المختصة بالنظر في تظلمات التوقيف؟", options: ["الشرطة", "المحكمة الجزائية", "ديوان المظالم", "وزارة الداخلية"], correct: 1, points: 10, reference: "المادة 119" },
    { id: 60, category: "التوقيف", question: "هل يُفرج عن الموقوف إذا انتهت مدة التوقيف؟", options: ["لا", "نعم ما لم يُمدد", "يبقى حتى المحاكمة", "ينقل للسجن"], correct: 1, points: 10, reference: "المادة 114" },

    // ===== المحاكمة (61-75) =====
    { id: 61, category: "المحاكمة", question: "ما مبدأ علنية الجلسات؟", options: ["سرية دائماً", "علنية إلا لاعتبارات", "حسب رغبة المتهم", "في الجنايات فقط"], correct: 1, points: 10, reference: "المادة 155" },
    { id: 62, category: "المحاكمة", question: "هل يجب حضور المتهم جلسات المحاكمة؟", options: ["لا", "نعم في الجرائم الكبرى", "اختياري", "بالوكالة دائماً"], correct: 1, points: 10, reference: "المادة 140" },
    { id: 63, category: "المحاكمة", question: "ما المقصود بالدفاع الشكلي؟", options: ["الدفاع عن الموضوع", "الدفع ببطلان الإجراءات", "طلب البراءة", "الاعتراف"], correct: 1, points: 15, reference: "أنواع الدفوع" },
    { id: 64, category: "المحاكمة", question: "متى يصدر الحكم الغيابي؟", options: ["إذا حضر المتهم", "إذا تخلف المتهم عن الحضور", "لا يجوز", "في المخالفات فقط"], correct: 1, points: 10, reference: "المادة 141" },
    { id: 65, category: "المحاكمة", question: "ما أثر الاعتراف أمام المحكمة؟", options: ["يُنهي الدعوى", "دليل يُقدره القاضي", "يُلزم المحكمة", "لا قيمة له"], correct: 1, points: 10, reference: "المادة 162" },
    { id: 66, category: "المحاكمة", question: "هل يجوز للمحكمة استجواب الشهود؟", options: ["لا", "نعم", "بطلب الخصوم فقط", "في الجنايات فقط"], correct: 1, points: 10, reference: "المادة 165" },
    { id: 67, category: "المحاكمة", question: "ما شروط صحة الشهادة؟", options: ["لا شروط", "البلوغ والعقل والعدالة", "القرابة", "الكتابة"], correct: 1, points: 10, reference: "المادة 164" },
    { id: 68, category: "المحاكمة", question: "هل يجوز رد القاضي؟", options: ["لا مطلقاً", "نعم لأسباب محددة", "بموافقة القاضي", "في الجنايات فقط"], correct: 1, points: 15, reference: "المادة 93" },
    { id: 69, category: "المحاكمة", question: "متى يجب على المحكمة النطق بالحكم؟", options: ["فوراً", "في جلسة علنية", "سراً", "بعد شهر"], correct: 1, points: 10, reference: "المادة 179" },
    { id: 70, category: "المحاكمة", question: "ما المقصود بمرافعة الخصوم؟", options: ["المحاكمة", "تقديم الأدلة والحجج", "الحكم", "التحقيق"], correct: 1, points: 10, reference: "إجراءات المحاكمة" },
    { id: 71, category: "المحاكمة", question: "هل يجوز تأجيل الجلسة؟", options: ["لا", "نعم لأسباب مقبولة", "مرة واحدة فقط", "بموافقة المتهم"], correct: 1, points: 10, reference: "المادة 153" },
    { id: 72, category: "المحاكمة", question: "من يبدأ بالمرافعة؟", options: ["المتهم", "المدعي العام ثم المجني عليه ثم المتهم", "القاضي", "الشهود"], correct: 1, points: 10, reference: "المادة 171" },
    { id: 73, category: "المحاكمة", question: "ما معنى ضبط الجلسة؟", options: ["كتابة المحضر", "حفظ النظام في القاعة", "تسجيل الحكم", "استدعاء الشهود"], correct: 1, points: 10, reference: "المادة 155" },
    { id: 74, category: "المحاكمة", question: "هل يجوز للمحكمة الحكم بأكثر مما طلبه المدعي؟", options: ["نعم", "لا", "في الجنايات", "بموافقة المتهم"], correct: 1, points: 15, reference: "قاعدة عدم الإضرار" },
    { id: 75, category: "المحاكمة", question: "ما المقصود بوقف الدعوى؟", options: ["إنهاؤها", "تعليق السير فيها مؤقتاً", "رفضها", "إحالتها"], correct: 1, points: 10, reference: "المادة 22" },

    // ===== الطعن في الأحكام (76-90) =====
    { id: 76, category: "الطعن", question: "ما المدة النظامية للاستئناف؟", options: ["10 أيام", "30 يوماً", "60 يوماً", "90 يوماً"], correct: 1, points: 10, reference: "المادة 194" },
    { id: 77, category: "الطعن", question: "من يحق له الاستئناف؟", options: ["المتهم فقط", "المحكوم عليه والمدعي العام والمدعي بالحق الخاص", "النيابة فقط", "القاضي"], correct: 1, points: 10, reference: "المادة 192" },
    { id: 78, category: "الطعن", question: "ما أثر الاستئناف على تنفيذ الحكم؟", options: ["لا أثر", "يوقف التنفيذ", "ينفذ الحكم", "يُخفف الحكم"], correct: 1, points: 10, reference: "المادة 196" },
    { id: 79, category: "الطعن", question: "هل يجوز الطعن بالنقض في الأحكام الجزائية؟", options: ["لا", "نعم في أحوال محددة", "دائماً", "في الجنايات فقط"], correct: 1, points: 15, reference: "المادة 198" },
    { id: 80, category: "الطعن", question: "ما المقصود بالتماس إعادة النظر؟", options: ["الاستئناف", "طعن استثنائي في أحكام نهائية", "النقض", "المراجعة"], correct: 1, points: 15, reference: "المادة 206" },
    { id: 81, category: "الطعن", question: "متى يجوز التماس إعادة النظر؟", options: ["دائماً", "إذا ظهرت أدلة جديدة أو حكم مناقض", "بعد سنة", "بموافقة النيابة"], correct: 1, points: 15, reference: "المادة 206" },
    { id: 82, category: "الطعن", question: "ما الجهة المختصة بنظر الاستئناف؟", options: ["نفس المحكمة", "محكمة الاستئناف", "المحكمة العليا", "ديوان المظالم"], correct: 1, points: 10, reference: "المادة 192" },
    { id: 83, category: "الطعن", question: "هل يجوز للمحكمة الاستئنافية تشديد العقوبة؟", options: ["نعم مطلقاً", "لا إذا كان المستأنف هو المتهم فقط", "دائماً", "بموافقة النيابة"], correct: 1, points: 15, reference: "المادة 200" },
    { id: 84, category: "الطعن", question: "ما المقصود بالحكم النهائي؟", options: ["الحكم الابتدائي", "الحكم غير القابل للطعن العادي", "حكم الإعدام", "الحكم المؤجل"], correct: 1, points: 10, reference: "تعريفات النظام" },
    { id: 85, category: "الطعن", question: "من يختص بنظر طلبات النقض؟", options: ["محكمة الاستئناف", "المحكمة العليا", "المحكمة الابتدائية", "ديوان المظالم"], correct: 1, points: 10, reference: "المادة 198" },
    { id: 86, category: "الطعن", question: "ما أسباب النقض؟", options: ["أي سبب", "مخالفة الشريعة أو خطأ في تطبيق النظام", "عدم الرضا", "طول المدة"], correct: 1, points: 10, reference: "المادة 198" },
    { id: 87, category: "الطعن", question: "هل يجوز الطعن في الأحكام الصادرة بالصلح؟", options: ["نعم", "لا كقاعدة", "بإذن القاضي", "بعد سنة"], correct: 1, points: 10, reference: "أحكام الصلح" },
    { id: 88, category: "الطعن", question: "ما أثر فوات مدة الاستئناف؟", options: ["لا أثر", "سقوط الحق في الاستئناف", "تمديد المدة", "قبول الاستئناف"], correct: 1, points: 10, reference: "المادة 194" },
    { id: 89, category: "الطعن", question: "هل يجوز الطعن لمصلحة القانون؟", options: ["لا", "نعم من النائب العام", "من المتهم", "من القاضي"], correct: 1, points: 15, reference: "الطعن لمصلحة القانون" },
    { id: 90, category: "الطعن", question: "ما المقصود بالإحالة للدائرة مصدرة الحكم؟", options: ["إلغاء الحكم", "إعادة القضية لنفس الدائرة للنظر مجدداً", "تأييد الحكم", "النقض"], correct: 1, points: 10, reference: "المادة 203" },

    // ===== تنفيذ الأحكام وحقوق المتهم (91-100) =====
    { id: 91, category: "تنفيذ الأحكام", question: "متى يُنفذ الحكم الجزائي؟", options: ["فوراً", "بعد صيرورته نهائياً", "بعد سنة", "بموافقة المتهم"], correct: 1, points: 10, reference: "المادة 210" },
    { id: 92, category: "تنفيذ الأحكام", question: "من يتولى تنفيذ الأحكام الجزائية؟", options: ["المحكمة", "النيابة العامة", "الشرطة", "وزارة الداخلية"], correct: 1, points: 10, reference: "المادة 210" },
    { id: 93, category: "تنفيذ الأحكام", question: "هل يجوز تأجيل تنفيذ عقوبة السجن؟", options: ["لا", "نعم لأسباب صحية أو إنسانية", "بطلب المحكوم عليه", "لمدة شهر"], correct: 1, points: 15, reference: "المادة 223" },
    { id: 94, category: "حقوق المتهم", question: "ما أهم حقوق المتهم في النظام؟", options: ["لا حقوق له", "البراءة حتى الإدانة والدفاع والمحامي", "الهروب", "رفض المحاكمة"], correct: 1, points: 10, reference: "المادة 2" },
    { id: 95, category: "حقوق المتهم", question: "هل يجوز تعذيب المتهم؟", options: ["نعم للاعتراف", "لا مطلقاً", "في الجنايات", "بإذن القاضي"], correct: 1, points: 10, reference: "المادة 2" },
    { id: 96, category: "حقوق المتهم", question: "ما حق المتهم في الاستعانة بمحامٍ؟", options: ["لا يحق له", "حق مكفول في جميع المراحل", "في المحاكمة فقط", "بإذن المحقق"], correct: 1, points: 10, reference: "المادة 4" },
    { id: 97, category: "حقوق المتهم", question: "ما المقصود بقرينة البراءة؟", options: ["إدانة المتهم", "اعتبار المتهم بريئاً حتى تثبت إدانته", "الاعتراف", "الصمت"], correct: 1, points: 10, reference: "المادة 2" },
    { id: 98, category: "حقوق المتهم", question: "هل يجب إبلاغ المتهم بالتهمة الموجهة إليه؟", options: ["لا", "نعم", "في الجنايات فقط", "بعد المحاكمة"], correct: 1, points: 10, reference: "المادة 102" },
    { id: 99, category: "حقوق المتهم", question: "ما حق المتهم في الترجمة؟", options: ["لا يحق له", "يحق له إذا لم يفهم اللغة العربية", "بأجر", "في المحاكمة فقط"], correct: 1, points: 10, reference: "المادة 96" },
    { id: 100, category: "تنفيذ الأحكام", question: "ما المقصود برد الاعتبار؟", options: ["إعادة المحاكمة", "إزالة آثار الحكم بعد انقضاء مدة", "البراءة", "العفو"], correct: 1, points: 15, reference: "المادة 225" }
  ];

  // ===== اختيار نوع المسابقة =====
  const [quizType, setQuizType] = useState('personal'); // 'personal' أو 'criminal'

  const startRaceGame = (type = 'personal') => {
    setQuizType(type);
    // اختيار الأسئلة حسب النوع
    const questionsSource = type === 'criminal' ? criminalProcedureQuestions : raceQuestionsData;
    // خلط الأسئلة واختيار 10 أسئلة عشوائية
    const shuffled = [...questionsSource].sort(() => Math.random() - 0.5).slice(0, 10);
    setRaceQuestions(shuffled);
    setCurrentRaceQuestion(0);
    setRaceScore(0);
    setOpponentScore(0);
    setPlayerPosition(0);
    setOpponentPosition(0);
    setRaceTimer(15); // زيادة الوقت لـ 15 ثانية
    setRaceAnswered(false);
    setRaceResult(null);
    setRaceGameActive(true);
    setShowRaceAnimation(true);
  };

  // مؤقت لعبة السباق
  useEffect(() => {
    let interval;
    if (raceGameActive && !raceAnswered && raceTimer > 0) {
      interval = setInterval(() => {
        setRaceTimer(prev => {
          if (prev <= 1) {
            // انتهى الوقت - الخصم يجيب
            handleRaceTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [raceGameActive, raceAnswered, raceTimer, currentRaceQuestion]);

  const handleRaceTimeout = () => {
    // الخصم يجيب صحيحاً بنسبة 60%
    const opponentCorrect = Math.random() < 0.6;
    if (opponentCorrect) {
      setOpponentScore(prev => prev + raceQuestions[currentRaceQuestion]?.points || 10);
      setOpponentPosition(prev => Math.min(prev + 20, 100));
    }
    setRaceAnswered(true);
    
    setTimeout(() => {
      moveToNextRaceQuestion();
    }, 1500);
  };

  const handleRaceAnswer = (answerIndex) => {
    if (raceAnswered) return;
    setRaceAnswered(true);
    
    const question = raceQuestions[currentRaceQuestion];
    const isCorrect = answerIndex === question.correct;
    
    // حركة اللاعب
    if (isCorrect) {
      setRaceScore(prev => prev + question.points);
      setPlayerPosition(prev => Math.min(prev + 20, 100));
    }
    
    // حركة الخصم (عشوائية بناءً على الوقت المتبقي)
    const opponentChance = 0.3 + (10 - raceTimer) * 0.05;
    const opponentCorrect = Math.random() < opponentChance;
    if (opponentCorrect && !isCorrect) {
      setOpponentScore(prev => prev + question.points);
      setOpponentPosition(prev => Math.min(prev + 20, 100));
    }
    
    setTimeout(() => {
      moveToNextRaceQuestion();
    }, 1500);
  };

  const moveToNextRaceQuestion = () => {
    if (currentRaceQuestion < raceQuestions.length - 1) {
      setCurrentRaceQuestion(prev => prev + 1);
      setRaceTimer(10);
      setRaceAnswered(false);
    } else {
      // انتهت اللعبة
      const won = raceScore > opponentScore || playerPosition > opponentPosition;
      setRaceResult({
        won,
        playerScore: raceScore,
        opponentScore: opponentScore,
        playerPosition,
        opponentPosition
      });
      setRaceGameActive(false);
    }
  };

  const resetRaceGame = () => {
    setRaceGameActive(false);
    setRaceResult(null);
    setShowRaceAnimation(false);
  };

  const handleSubmitGame = async () => {
    setLoading(true);
    
    // إذا كان المستخدم مسجل دخول، نرسل للـ API
    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/api/virtual-court/prosecutor-game/submit`, {
          scenario_id: selectedScenario.id,
          selected_culprits: selectedCulprits,
          selected_evidence: selectedEvidence,
          accusation: accusation,
          selected_articles: selectedArticles,
          time_taken: gameTimer
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setGameResult(res.data);
        setGameInProgress(false);
      } catch (err) {
        console.error('Error submitting game:', err);
        alert('حدث خطأ أثناء تقديم الإجابة');
      } finally {
        setLoading(false);
      }
    } else {
      // للمستخدمين غير المسجلين - نحسب النتيجة محلياً بدون حفظ
      try {
        // جلب السيناريو الكامل مع الإجابات الصحيحة من البيانات المحلية
        const res = await axios.get(`${API}/api/virtual-court/prosecutor-game/scenarios`);
        const scenarios = res.data.scenarios;
        const currentScenario = scenarios.find(s => s.id === selectedScenario.id);
        
        // حساب بسيط للنتيجة (تقريبي)
        const maxScore = selectedScenario.points;
        let score = 0;
        const feedback = [];
        
        // نعطي نقاط على أساس عدد الأدلة المختارة
        if (selectedEvidence.length > 0) {
          score += Math.round(maxScore * 0.25);
          feedback.push({type: 'partial', message: `اخترت ${selectedEvidence.length} دليل`});
        }
        
        // نقاط على اختيار الجناة
        if (selectedCulprits.length > 0) {
          score += Math.round(maxScore * 0.3);
          feedback.push({type: 'partial', message: `اخترت ${selectedCulprits.length} مشتبه به`});
        }
        
        // نقاط على التهمة
        if (accusation.trim()) {
          score += Math.round(maxScore * 0.25);
          feedback.push({type: 'partial', message: 'كتبت التهمة'});
        }
        
        // نقاط على المواد
        if (selectedArticles.length > 0) {
          score += Math.round(maxScore * 0.2);
          feedback.push({type: 'partial', message: `اخترت ${selectedArticles.length} مادة قانونية`});
        }
        
        feedback.push({type: 'info', message: '⚠️ سجل الدخول لرؤية الإجابات الصحيحة وحفظ نتيجتك'});
        
        setGameResult({
          score: score,
          max_score: maxScore,
          passed: score >= (maxScore * 0.5),
          feedback: feedback,
          correct_answers: null, // لا نعرض الإجابات الصحيحة للزوار
          is_guest: true
        });
        setGameInProgress(false);
      } catch (err) {
        console.error('Error:', err);
        alert('حدث خطأ');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleEvidence = (evidenceId) => {
    setSelectedEvidence(prev =>
      prev.includes(evidenceId)
        ? prev.filter(id => id !== evidenceId)
        : [...prev, evidenceId]
    );
  };

  const toggleCulprit = (culpritId) => {
    setSelectedCulprits(prev =>
      prev.includes(culpritId)
        ? prev.filter(id => id !== culpritId)
        : [...prev, culpritId]
    );
  };

  const toggleArticle = (articleText) => {
    setSelectedArticles(prev =>
      prev.includes(articleText)
        ? prev.filter(a => a !== articleText)
        : [...prev, articleText]
    );
  };

  // الأدوار المتاحة
  const roles = [
    { id: 'judge', name: 'القاضي', icon: Crown, color: 'bg-yellow-500', description: 'رئاسة الجلسة وإدارة المحاكمة' },
    { id: 'prosecutor', name: 'النيابة العامة', icon: Shield, color: 'bg-red-500', description: 'تمثيل الادعاء العام' },
    { id: 'defense', name: 'محامي الدفاع', icon: Briefcase, color: 'bg-blue-500', description: 'الدفاع عن المتهم' },
    { id: 'witness', name: 'الشاهد', icon: UserCircle, color: 'bg-purple-500', description: 'الإدلاء بالشهادة' },
  ];

  // القضايا التدريبية للمحكمة
  const trainingCases = [
    {
      id: 1,
      title: 'قضية سرقة - جنائي',
      type: 'جنائي',
      difficulty: 'مبتدئ',
      description: 'متهم بسرقة متجر تجاري',
      parties: { plaintiff: 'النيابة العامة', defendant: 'محمد أحمد' },
      facts: 'تم القبض على المتهم بعد خروجه من متجر وبحوزته بضائع لم يدفع ثمنها',
    },
    {
      id: 2,
      title: 'نزاع عقاري - مدني',
      type: 'مدني',
      difficulty: 'متوسط',
      description: 'نزاع على ملكية عقار',
      parties: { plaintiff: 'خالد العتيبي', defendant: 'شركة البناء المتحدة' },
      facts: 'يدعي المدعي ملكيته لقطعة أرض تم البناء عليها من قبل المدعى عليه',
    },
  ];

  // مراحل المحاكمة
  const phases = [
    { id: 'opening', name: 'افتتاح الجلسة', icon: Gavel },
    { id: 'claims', name: 'عرض الدعوى', icon: FileText },
    { id: 'evidence', name: 'تقديم الأدلة', icon: BookOpen },
    { id: 'arguments', name: 'المرافعات', icon: MessageSquare },
    { id: 'verdict', name: 'النطق بالحكم', icon: Gavel },
  ];

  // عبارات المحاكمة الجاهزة
  const courtPhrases = {
    judge: [
      'المحكمة منعقدة، بسم الله نبدأ الجلسة',
      'هل المدعي حاضر؟',
      'هل المدعى عليه حاضر؟',
      'اعرض دعواك يا مدعي',
    ],
    prosecutor: [
      'الادعاء العام يطالب بتطبيق أقصى العقوبة',
      'لدينا أدلة قاطعة على ارتكاب الجريمة',
    ],
    defense: [
      'موكلي بريء حتى تثبت إدانته',
      'نطلب البراءة لعدم كفاية الأدلة',
    ],
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !selectedRole) return;
    
    const roleInfo = roles.find(r => r.id === selectedRole);
    setMessages([...messages, {
      id: Date.now(),
      role: selectedRole,
      roleName: roleInfo.name,
      text: newMessage,
      time: new Date().toLocaleTimeString('ar-SA'),
    }]);
    setNewMessage('');
  };

  const startSession = () => {
    if (!selectedCase || !selectedRole) {
      alert('الرجاء اختيار القضية والدور أولاً');
      return;
    }
    setSessionActive(true);
    setActiveTab('courtroom');
    setMessages([{
      id: Date.now(),
      role: 'system',
      roleName: 'النظام',
      text: `بدأت جلسة المحاكمة في القضية: ${selectedCase.title}`,
      time: new Date().toLocaleTimeString('ar-SA'),
    }]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary to-primary-800" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowRight className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gold rounded-lg">
                <Scale className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-white">المحكمة الافتراضية الأهلية</h1>
                <p className="text-white/60 text-sm">منصة تدريبية لمحاكاة المحاكم</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {gameInProgress && selectedScenario && (
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5 text-gold" />
                <span className={`text-white font-mono text-lg ${gameTimer > selectedScenario.time_limit * 50 ? 'text-red-400' : ''}`}>
                  {formatTime(gameTimer)} / {formatTime(selectedScenario.time_limit * 60)}
                </span>
              </div>
            )}
            
            {!isLoggedIn ? (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark"
                data-testid="login-btn"
              >
                تسجيل الدخول
              </button>
            ) : (
              <div className="flex items-center gap-2 text-white">
                <UserCircle className="w-6 h-6" />
                <span className="text-sm">مسجل</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'home', name: 'الرئيسية', icon: Scale },
              { id: 'games', name: 'الألعاب القانونية', icon: Target },
              { id: 'courtroom', name: 'قاعة المحكمة', icon: Gavel },
              { id: 'leaderboard', name: 'المتصدرون', icon: Trophy },
              { id: 'profile', name: 'ملفي', icon: User },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-6 py-4 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gold text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        
        {/* الصفحة الرئيسية */}
        {activeTab === 'home' && (
          <div className="space-y-8">
            {/* مقدمة */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
              <Scale className="w-16 h-16 text-gold mx-auto mb-4" />
              <h2 className="text-3xl font-heading font-bold text-white mb-4">
                مرحباً بك في المحكمة الافتراضية الأهلية
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
                أول منصة عربية متكاملة لمحاكاة المحاكم السعودية وتدريب المحامين والقانونيين
              </p>
            </div>

            {/* الألعاب المميزة */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* لعبة التحدي القانوني */}
              <div 
                onClick={() => setActiveTab('games')}
                className="bg-gradient-to-br from-emerald-600 to-teal-800 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-transform relative overflow-hidden"
                data-testid="prosecutor-game-card"
              >
                {/* تأثير متحرك */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-gold to-yellow-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <Swords className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-2xl font-bold text-white">لعبة التحدي القانوني</h3>
                    <Flame className="w-5 h-5 text-orange-400 animate-pulse" />
                  </div>
                  <p className="text-white/80 text-sm mb-4">حلل الأدلة، حدد الجناة، واختر التهمة والمواد القانونية المناسبة</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gold text-sm font-medium">
                      <Play className="w-4 h-4" />
                      <span>5 تحديات متاحة</span>
                    </div>
                    <div className="px-3 py-1 bg-white/20 rounded-full text-white text-xs">
                      🔥 الأكثر شعبية
                    </div>
                  </div>
                </div>
              </div>

              {/* لعبة المرافعة الذهبية */}
              <div 
                onClick={() => setActiveTab('games')}
                className="bg-gradient-to-br from-amber-500 to-yellow-700 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-transform relative overflow-hidden"
                data-testid="pleading-game-card"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">المرافعة الذهبية</h3>
                <p className="text-white/80 text-sm mb-4">قدم مرافعتك وأقنع القاضي بحجتك</p>
                <div className="flex items-center gap-2 text-gold text-sm font-medium">
                  <Play className="w-4 h-4" />
                  <span>5 قضايا متاحة</span>
                </div>
              </div>

              {/* لعبة الخطأ الإجرائي */}
              <div 
                onClick={() => setActiveTab('games')}
                className="bg-gradient-to-br from-purple-600 to-indigo-800 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-transform relative overflow-hidden"
                data-testid="error-game-card"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">الخطأ الإجرائي</h3>
                <p className="text-white/80 text-sm mb-4">اكتشف الأخطاء الإجرائية في المحاكمات</p>
                <div className="flex items-center gap-2 text-gold text-sm font-medium">
                  <Play className="w-4 h-4" />
                  <span>5 سيناريوهات متاحة</span>
                </div>
              </div>
            </div>

            {/* إحصائيات سريعة */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-gold mb-1">15</div>
                <div className="text-white/60 text-sm">سيناريو</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-gold mb-1">3</div>
                <div className="text-white/60 text-sm">ألعاب</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-gold mb-1">8</div>
                <div className="text-white/60 text-sm">أدوار قضائية</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-gold mb-1">∞</div>
                <div className="text-white/60 text-sm">تعلم مستمر</div>
              </div>
            </div>
          </div>
        )}

        {/* صفحة الألعاب */}
        {activeTab === 'games' && !gameInProgress && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-white">الألعاب القانونية</h2>
              <div className="flex items-center gap-2 text-white/60">
                <Target className="w-5 h-5" />
                <span>اختر لعبة للبدء</span>
              </div>
            </div>

            {/* لعبة التحدي القانوني */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-gold to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <Swords className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-2xl font-bold text-white">لعبة التحدي القانوني</h3>
                      <Flame className="w-5 h-5 text-orange-400 animate-pulse" />
                    </div>
                    <p className="text-white/80">تحدَّ نفسك في تحليل القضايا واختيار القرارات القانونية الصحيحة</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h4 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-600" />
                  التحديات المتاحة:
                </h4>
                <div className="space-y-4">
                  {scenarios.map(scenario => (
                    <div 
                      key={scenario.id}
                      className="border border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h5 className="font-bold text-primary">{scenario.title}</h5>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              scenario.difficulty === 'مبتدئ' ? 'bg-green-100 text-green-700' :
                              scenario.difficulty === 'متوسط' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {scenario.difficulty}
                            </span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                              {scenario.crime_type}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{scenario.case_summary}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {scenario.time_limit} دقيقة
                            </span>
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-gold" />
                              {scenario.points} نقطة
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {scenario.evidence_list?.length || 0} دليل
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => startGame(scenario)}
                          data-testid={`start-game-${scenario.id}`}
                          className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 font-medium"
                        >
                          <Swords className="w-5 h-5" />
                          ابدأ التحدي
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {!isLoggedIn && (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                    <p className="text-emerald-800">
                      💡 يمكنك تجربة التحدي الآن! <button onClick={() => navigate('/login')} className="text-emerald-700 font-bold underline">سجل الدخول</button> لحفظ نتائجك في لوحة المتصدرين
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* لعبة المرافعة الذهبية */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl mt-8">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-600 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">لعبة المرافعة الذهبية</h3>
                    <p className="text-white/80">اختر أقوى الحجج للدفاع عن موكلك</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h4 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-amber-500" />
                  القضايا المتاحة:
                </h4>
                <div className="space-y-4">
                  {pleadingScenarios.map(scenario => (
                    <div 
                      key={scenario.id}
                      className="border border-gray-200 rounded-xl p-4 hover:border-amber-300 hover:bg-amber-50/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h5 className="font-bold text-primary">{scenario.title}</h5>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              scenario.difficulty === 'مبتدئ' ? 'bg-green-100 text-green-700' :
                              scenario.difficulty === 'متوسط' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {scenario.difficulty}
                            </span>
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                              {scenario.case_type}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{scenario.situation?.substring(0, 100)}...</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {scenario.time_limit} دقيقة
                            </span>
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-gold" />
                              {scenario.points} نقطة
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => startGame(scenario, 'pleading')}
                          data-testid={`start-pleading-${scenario.id}`}
                          className="px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 font-medium"
                        >
                          <MessageSquare className="w-5 h-5" />
                          ابدأ
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* لعبة الخطأ الإجرائي */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl mt-8">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">لعبة الخطأ الإجرائي</h3>
                    <p className="text-white/80">اكتشف الأخطاء في إجراءات المحاكمة</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h4 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-purple-500" />
                  السيناريوهات المتاحة:
                </h4>
                <div className="space-y-4">
                  {errorScenarios.map(scenario => (
                    <div 
                      key={scenario.id}
                      className="border border-gray-200 rounded-xl p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h5 className="font-bold text-primary">{scenario.title}</h5>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              scenario.difficulty === 'مبتدئ' ? 'bg-green-100 text-green-700' :
                              scenario.difficulty === 'متوسط' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {scenario.difficulty}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-3">{scenario.case_description?.substring(0, 100)}...</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {scenario.time_limit} دقيقة
                            </span>
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-gold" />
                              {scenario.points} نقطة
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => startGame(scenario, 'error')}
                          data-testid={`start-error-${scenario.id}`}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium"
                        >
                          <AlertTriangle className="w-5 h-5" />
                          ابدأ
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* لعبة سباق المحكمة - الحركية التنافسية */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl mt-8">
              <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-600 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center animate-pulse">
                    <Flame className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                      سباق المحكمة
                      <span className="text-sm bg-white/20 px-2 py-1 rounded-full">جديد!</span>
                    </h3>
                    <p className="text-white/80">تنافس مباشر! أجب بسرعة قبل خصمك</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {!raceGameActive && !raceResult && (
                  <div className="text-center py-8">
                    <div className="w-24 h-24 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Flame className="w-12 h-12 text-rose-500" />
                    </div>
                    <h4 className="text-xl font-bold text-primary mb-2">هل أنت مستعد للتحدي؟</h4>
                    <p className="text-gray-600 mb-6">سابق خصمك الافتراضي وأجب على الأسئلة القانونية بأسرع وقت!</p>
                    <div className="flex items-center justify-center gap-8 mb-6">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <User className="w-8 h-8 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-600">أنت</span>
                      </div>
                      <div className="text-3xl font-bold text-gray-300">VS</div>
                      <div className="text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Zap className="w-8 h-8 text-red-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-600">الخصم</span>
                      </div>
                    </div>
                    <button
                      onClick={startRaceGame}
                      className="px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 font-bold text-lg flex items-center gap-3 mx-auto shadow-lg transform hover:scale-105 transition-all"
                    >
                      <Flame className="w-6 h-6" />
                      ابدأ السباق!
                    </button>
                  </div>
                )}

                {/* اللعبة جارية */}
                {raceGameActive && raceQuestions.length > 0 && (
                  <div className="space-y-6">
                    {/* شريط السباق المرئي */}
                    <div className="bg-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-600">أنت</span>
                        <span className="text-sm font-medium text-red-600">الخصم</span>
                      </div>
                      <div className="relative h-16 bg-gradient-to-r from-blue-100 via-gray-200 to-red-100 rounded-lg overflow-hidden">
                        {/* خط النهاية */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gold z-10">
                          <Trophy className="w-6 h-6 text-gold absolute -top-1 -left-2.5" />
                        </div>
                        
                        {/* اللاعب */}
                        <div 
                          className="absolute bottom-2 transition-all duration-500 ease-out"
                          style={{ left: `${playerPosition * 0.45}%` }}
                        >
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white animate-bounce">
                            <User className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        
                        {/* الخصم */}
                        <div 
                          className="absolute top-2 transition-all duration-500 ease-out"
                          style={{ left: `${opponentPosition * 0.45}%` }}
                        >
                          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                            <Zap className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2 text-sm">
                        <span className="font-bold text-blue-600">{raceScore} نقطة</span>
                        <span className="font-bold text-red-600">{opponentScore} نقطة</span>
                      </div>
                    </div>

                    {/* المؤقت */}
                    <div className="flex items-center justify-center">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold ${
                        raceTimer <= 3 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-gold/20 text-gold'
                      }`}>
                        {raceTimer}
                      </div>
                    </div>

                    {/* السؤال */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-gray-500">السؤال {currentRaceQuestion + 1} من {raceQuestions.length}</span>
                        <span className="px-3 py-1 bg-gold/20 text-gold rounded-full text-sm font-bold">
                          +{raceQuestions[currentRaceQuestion]?.points} نقطة
                        </span>
                      </div>
                      <h4 className="text-xl font-bold text-primary mb-6">
                        {raceQuestions[currentRaceQuestion]?.question}
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {raceQuestions[currentRaceQuestion]?.options.map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleRaceAnswer(idx)}
                            disabled={raceAnswered}
                            className={`p-4 rounded-xl text-lg font-medium transition-all transform hover:scale-102 ${
                              raceAnswered 
                                ? idx === raceQuestions[currentRaceQuestion].correct
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-200 text-gray-500'
                                : 'bg-white border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* نتيجة السباق */}
                {raceResult && (
                  <div className="text-center py-8">
                    <div className={`w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 ${
                      raceResult.won 
                        ? 'bg-gradient-to-br from-gold to-yellow-500' 
                        : 'bg-gradient-to-br from-gray-400 to-gray-600'
                    }`}>
                      {raceResult.won ? (
                        <Trophy className="w-16 h-16 text-white" />
                      ) : (
                        <Shield className="w-16 h-16 text-white" />
                      )}
                    </div>
                    <h3 className={`text-3xl font-bold mb-4 ${raceResult.won ? 'text-gold' : 'text-gray-600'}`}>
                      {raceResult.won ? '🎉 فزت بالسباق!' : 'حاول مرة أخرى!'}
                    </h3>
                    
                    <div className="flex items-center justify-center gap-12 mb-6">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <User className="w-8 h-8 text-blue-600" />
                        </div>
                        <p className="text-2xl font-bold text-blue-600">{raceResult.playerScore}</p>
                        <p className="text-sm text-gray-500">نقاطك</p>
                      </div>
                      <div className="text-4xl font-bold text-gray-300">VS</div>
                      <div className="text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Zap className="w-8 h-8 text-red-600" />
                        </div>
                        <p className="text-2xl font-bold text-red-600">{raceResult.opponentScore}</p>
                        <p className="text-sm text-gray-500">نقاط الخصم</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={resetRaceGame}
                      className="px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 font-bold text-lg flex items-center gap-3 mx-auto"
                    >
                      <RotateCcw className="w-5 h-5" />
                      العب مرة أخرى
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* لعبة التحدي القانوني - أثناء اللعب */}
        {activeTab === 'prosecutor-game' && gameInProgress && selectedScenario && currentGameType === 'prosecutor' && (
          <div className="space-y-6">
            {/* شريط التقدم */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                    <Swords className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary">{selectedScenario.title}</h3>
                    <p className="text-xs text-gray-500">لعبة التحدي القانوني</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">الخطوة {gameStep} من 4</span>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map(step => (
                  <div 
                    key={step}
                    className={`flex-1 h-2 rounded-full ${
                      step < gameStep ? 'bg-green-500' :
                      step === gameStep ? 'bg-gold' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>الأدلة</span>
                <span>الجناة</span>
                <span>التهمة</span>
                <span>المواد</span>
              </div>
            </div>

            {/* ملخص القضية */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="font-bold text-primary mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" />
                ملخص القضية
              </h4>
              <p className="text-gray-700">{selectedScenario.case_summary}</p>
            </div>

            {/* الخطوة 1: اختيار الأدلة */}
            {gameStep === 1 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-gold" />
                  اختر الأدلة المهمة للقضية
                </h4>
                <p className="text-gray-500 text-sm mb-4">اختر الأدلة التي تدعم الاتهام (يمكنك اختيار أكثر من دليل)</p>
                
                <div className="space-y-3">
                  {selectedScenario.evidence_list?.map(evidence => (
                    <div 
                      key={evidence.id}
                      onClick={() => toggleEvidence(evidence.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedEvidence.includes(evidence.id)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gold'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedEvidence.includes(evidence.id) ? 'border-green-500 bg-green-500' : 'border-gray-300'
                        }`}>
                          {selectedEvidence.includes(evidence.id) && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs mb-1 ${
                            evidence.type === 'video' ? 'bg-blue-100 text-blue-700' :
                            evidence.type === 'physical' ? 'bg-purple-100 text-purple-700' :
                            evidence.type === 'digital' ? 'bg-cyan-100 text-cyan-700' :
                            evidence.type === 'witness' ? 'bg-orange-100 text-orange-700' :
                            evidence.type === 'document' ? 'bg-green-100 text-green-700' :
                            evidence.type === 'financial' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {evidence.type === 'video' ? 'فيديو' :
                             evidence.type === 'physical' ? 'دليل مادي' :
                             evidence.type === 'digital' ? 'رقمي' :
                             evidence.type === 'witness' ? 'شهادة' :
                             evidence.type === 'document' ? 'مستند' :
                             evidence.type === 'financial' ? 'مالي' : 'آخر'}
                          </span>
                          <p className="text-gray-700">{evidence.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setGameStep(2)}
                    disabled={selectedEvidence.length === 0}
                    className="px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50 flex items-center gap-2"
                  >
                    التالي
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* الخطوة 2: تحديد الجناة */}
            {gameStep === 2 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gold" />
                  حدد الجاني/الجناة
                </h4>
                <p className="text-gray-500 text-sm mb-4">من هو المتهم/المتهمون بناءً على الأدلة؟</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedScenario.suspects?.map(suspect => (
                    <div 
                      key={suspect.id}
                      onClick={() => toggleCulprit(suspect.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedCulprits.includes(suspect.id)
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gold'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          selectedCulprits.includes(suspect.id) ? 'bg-red-500' : 'bg-gray-200'
                        }`}>
                          <User className={`w-6 h-6 ${selectedCulprits.includes(suspect.id) ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <h5 className="font-bold text-primary">{suspect.name}</h5>
                          <p className="text-gray-500 text-sm">{suspect.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => setGameStep(1)}
                    className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                  >
                    <ArrowRight className="w-5 h-5" />
                    السابق
                  </button>
                  <button
                    onClick={() => setGameStep(3)}
                    disabled={selectedCulprits.length === 0}
                    className="px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50 flex items-center gap-2"
                  >
                    التالي
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* الخطوة 3: كتابة التهمة */}
            {gameStep === 3 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gold" />
                  صياغة التهمة
                </h4>
                <p className="text-gray-500 text-sm mb-4">اكتب التهمة الموجهة للمتهم/المتهمين</p>
                
                <textarea
                  value={accusation}
                  onChange={(e) => setAccusation(e.target.value)}
                  placeholder="مثال: جريمة سرقة موصوفة / احتيال مالي وتزوير مستندات..."
                  className="w-full p-4 border border-gray-200 rounded-lg h-32 focus:ring-2 focus:ring-gold focus:border-gold"
                />

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">أمثلة على التهم:</p>
                  <div className="flex flex-wrap gap-2">
                    {['سرقة', 'احتيال مالي', 'تزوير', 'ابتزاز', 'قتل خطأ', 'غسيل أموال'].map(example => (
                      <button
                        key={example}
                        onClick={() => setAccusation(prev => prev ? `${prev} و${example}` : example)}
                        className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm hover:bg-gold/10"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => setGameStep(2)}
                    className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                  >
                    <ArrowRight className="w-5 h-5" />
                    السابق
                  </button>
                  <button
                    onClick={() => setGameStep(4)}
                    disabled={!accusation.trim()}
                    className="px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50 flex items-center gap-2"
                  >
                    التالي
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* الخطوة 4: اختيار المواد القانونية */}
            {gameStep === 4 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-gold" />
                  المواد القانونية
                </h4>
                <p className="text-gray-500 text-sm mb-4">اختر المواد القانونية المناسبة للتهمة</p>
                
                <div className="grid md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                  {legalArticles.map(article => (
                    <div 
                      key={article.id}
                      onClick={() => toggleArticle(article.text)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${
                        selectedArticles.includes(article.text)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gold'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          selectedArticles.includes(article.text) ? 'border-green-500 bg-green-500' : 'border-gray-300'
                        }`}>
                          {selectedArticles.includes(article.text) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-gray-700">{article.text}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ملخص الإجابات */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-bold text-primary mb-3">ملخص إجابتك:</h5>
                  <div className="space-y-2 text-sm">
                    <p><strong>الأدلة المختارة:</strong> {selectedEvidence.length} دليل</p>
                    <p><strong>المتهمون:</strong> {selectedScenario.suspects?.filter(s => selectedCulprits.includes(s.id)).map(s => s.name).join('، ') || 'لم يتم الاختيار'}</p>
                    <p><strong>التهمة:</strong> {accusation || 'لم تُكتب'}</p>
                    <p><strong>المواد القانونية:</strong> {selectedArticles.length} مادة</p>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => setGameStep(3)}
                    className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                  >
                    <ArrowRight className="w-5 h-5" />
                    السابق
                  </button>
                  <button
                    onClick={handleSubmitGame}
                    disabled={loading || selectedArticles.length === 0}
                    data-testid="submit-game-btn"
                    className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 flex items-center gap-2 font-bold shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        جاري التقييم...
                      </>
                    ) : (
                      <>
                        <Swords className="w-5 h-5" />
                        إنهاء التحدي
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* نتيجة اللعبة */}
        {activeTab === 'prosecutor-game' && gameResult && (
          <div className="space-y-6">
            <div className={`bg-white rounded-2xl p-8 shadow-xl text-center ${
              gameResult.passed ? 'border-4 border-green-500' : 'border-4 border-red-500'
            }`}>
              <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
                gameResult.passed ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {gameResult.passed ? (
                  <Trophy className="w-12 h-12 text-green-600" />
                ) : (
                  <X className="w-12 h-12 text-red-600" />
                )}
              </div>
              
              <h2 className={`text-3xl font-bold mb-4 ${gameResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                {gameResult.passed ? 'مبروك! اجتزت التحدي' : 'لم تجتز هذه المرة'}
              </h2>
              
              <div className="text-6xl font-bold text-primary mb-2">
                {gameResult.score} <span className="text-2xl text-gray-400">/ {gameResult.max_score}</span>
              </div>
              <p className="text-gray-500 mb-8">نقطة</p>

              {/* التقييم التفصيلي */}
              <div className="bg-gray-50 rounded-xl p-6 text-right mb-6">
                <h4 className="font-bold text-primary mb-4">التقييم التفصيلي:</h4>
                <div className="space-y-3">
                  {gameResult.feedback?.map((fb, idx) => (
                    <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                      fb.type === 'success' ? 'bg-green-50' :
                      fb.type === 'partial' ? 'bg-yellow-50' :
                      fb.type === 'bonus' ? 'bg-blue-50' :
                      fb.type === 'info' ? 'bg-purple-50' : 'bg-red-50'
                    }`}>
                      {fb.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                      {fb.type === 'partial' && <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />}
                      {fb.type === 'bonus' && <Star className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                      {fb.type === 'info' && <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />}
                      {fb.type === 'error' && <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                      <span className={`text-sm ${
                        fb.type === 'success' ? 'text-green-700' :
                        fb.type === 'partial' ? 'text-yellow-700' :
                        fb.type === 'bonus' ? 'text-blue-700' :
                        fb.type === 'info' ? 'text-purple-700' : 'text-red-700'
                      }`}>{fb.message}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* الإجابات الصحيحة - فقط للمستخدمين المسجلين */}
              {gameResult.correct_answers && (
                <div className="bg-blue-50 rounded-xl p-6 text-right mb-6">
                  <h4 className="font-bold text-primary mb-4">الإجابات الصحيحة:</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>الجناة:</strong> {gameResult.correct_answers?.culprits?.join('، ')}</p>
                    <p><strong>التهمة:</strong> {gameResult.correct_answers?.accusation}</p>
                    <p><strong>المواد القانونية:</strong></p>
                    <ul className="list-disc list-inside mr-4">
                      {gameResult.correct_answers?.articles?.map((art, i) => (
                        <li key={i}>{art}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* رسالة للزوار */}
              {gameResult.is_guest && (
                <div className="bg-gradient-to-r from-gold/20 to-yellow-100 rounded-xl p-6 text-center mb-6 border-2 border-gold">
                  <h4 className="font-bold text-primary mb-2">🎯 تريد معرفة الإجابات الصحيحة؟</h4>
                  <p className="text-gray-600 mb-4">سجل الدخول لرؤية الإجابات الصحيحة وحفظ نتيجتك في لوحة المتصدرين!</p>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-8 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark font-bold"
                  >
                    تسجيل الدخول الآن
                  </button>
                </div>
              )}

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    setGameResult(null);
                    setActiveTab('games');
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
                >
                  <ArrowRight className="w-5 h-5" />
                  العودة للألعاب
                </button>
                <button
                  onClick={() => startGame(selectedScenario, currentGameType)}
                  className="px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark flex items-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  إعادة المحاولة
                </button>
              </div>
            </div>
          </div>
        )}

        {/* لعبة المرافعة الذهبية - أثناء اللعب */}
        {activeTab === 'prosecutor-game' && gameInProgress && selectedScenario && currentGameType === 'pleading' && (
          <div className="space-y-6">
            {/* شريط التقدم */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary">{selectedScenario.title}</h3>
                    <p className="text-xs text-gray-500">لعبة المرافعة الذهبية</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-lg">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-700 font-mono">{formatTime(gameTimer)}</span>
                </div>
              </div>
            </div>

            {/* الموقف */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="font-bold text-primary mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                موقف القضية
              </h4>
              <p className="text-gray-700 mb-4">{selectedScenario.situation}</p>
              <div className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                دورك: {selectedScenario.your_role}
              </div>
            </div>

            {/* حجج الخصم */}
            <div className="bg-red-50 rounded-xl p-6 shadow-lg border border-red-200">
              <h4 className="font-bold text-red-700 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                حجج الخصم:
              </h4>
              <ul className="space-y-2">
                {selectedScenario.opponent_arguments?.map((arg, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-red-600">
                    <span className="text-red-400">•</span>
                    {arg}
                  </li>
                ))}
              </ul>
            </div>

            {/* اختيار الحجج */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-amber-500" />
                اختر حججك للدفاع (يمكنك اختيار أكثر من حجة):
              </h4>
              <div className="space-y-3">
                {selectedScenario.available_defenses?.map(defense => (
                  <div 
                    key={defense.id}
                    onClick={() => toggleDefense(defense.id)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedDefenses.includes(defense.id)
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedDefenses.includes(defense.id) ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                      }`}>
                        {selectedDefenses.includes(defense.id) && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <p className="text-gray-700">{defense.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSubmitPleadingGame}
                  disabled={loading || selectedDefenses.length === 0}
                  className="px-8 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg hover:from-amber-600 hover:to-yellow-700 disabled:opacity-50 flex items-center gap-2 font-bold shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      جاري التقييم...
                    </>
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5" />
                      تقديم المرافعة
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* لعبة الخطأ الإجرائي - أثناء اللعب */}
        {activeTab === 'prosecutor-game' && gameInProgress && selectedScenario && currentGameType === 'error' && (
          <div className="space-y-6">
            {/* شريط التقدم */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary">{selectedScenario.title}</h3>
                    <p className="text-xs text-gray-500">لعبة الخطأ الإجرائي</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-lg">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span className="text-purple-700 font-mono">{formatTime(gameTimer)}</span>
                </div>
              </div>
            </div>

            {/* وصف القضية */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="font-bold text-primary mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                وصف القضية
              </h4>
              <p className="text-gray-700">{selectedScenario.case_description}</p>
            </div>

            {/* إجراءات المحكمة */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-lg">
              <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-purple-500" />
                ما حدث في المحاكمة:
              </h4>
              <ol className="space-y-3">
                {selectedScenario.court_proceedings?.map((proc, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-gray-700">{proc}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* تحديد الأخطاء */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-purple-500" />
                حدد الأخطاء الإجرائية:
              </h4>
              <p className="text-gray-500 text-sm mb-4">اختر الإجراءات التي تعتقد أنها أخطاء قانونية</p>
              <div className="space-y-3">
                {selectedScenario.errors?.map(error => (
                  <div 
                    key={error.id}
                    onClick={() => toggleError(error.id)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedErrors.includes(error.id)
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedErrors.includes(error.id) ? 'border-red-500 bg-red-500' : 'border-gray-300'
                      }`}>
                        {selectedErrors.includes(error.id) && <X className="w-4 h-4 text-white" />}
                      </div>
                      <p className="text-gray-700">{error.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSubmitErrorGame}
                  disabled={loading || selectedErrors.length === 0}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-purple-700 hover:to-indigo-800 disabled:opacity-50 flex items-center gap-2 font-bold shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      جاري التقييم...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5" />
                      تقديم الإجابة
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* المتصدرون */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-heading font-bold text-white mb-6">لوحة المتصدرين</h2>
            
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {leaderboard.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-600">الترتيب</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-600">اللاعب</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-600">مجموع النقاط</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-600">الألعاب</th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-600">نسبة الفوز</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leaderboard.map((player, idx) => (
                      <tr key={player.user_id} className={idx < 3 ? 'bg-gold/5' : ''}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {player.rank === 1 && <Crown className="w-5 h-5 text-yellow-500" />}
                            {player.rank === 2 && <Award className="w-5 h-5 text-gray-400" />}
                            {player.rank === 3 && <Award className="w-5 h-5 text-orange-400" />}
                            <span className="font-bold">{player.rank}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-medium text-primary">{player.user_name}</td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-gold">{player.total_score}</span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{player.games_played}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-sm ${
                            player.win_rate >= 70 ? 'bg-green-100 text-green-700' :
                            player.win_rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {player.win_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>لا توجد نتائج بعد. كن أول المتصدرين!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ملفي الشخصي */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {isLoggedIn && userProfile ? (
              <>
                {/* بطاقة الملف الشخصي */}
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <div className="flex items-center gap-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center">
                      <span className="text-4xl font-bold text-white">{userProfile.level}</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-primary">{userProfile.user_name}</h2>
                      <p className="text-gold font-medium">{userProfile.rank}</p>
                      <p className="text-gray-500">الترتيب العام: {userProfile.global_rank} من {userProfile.total_players}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-8">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-3xl font-bold text-gold">{userProfile.total_xp}</div>
                      <div className="text-gray-500 text-sm">XP</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-3xl font-bold text-primary">{userProfile.games_played}</div>
                      <div className="text-gray-500 text-sm">ألعاب</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-3xl font-bold text-green-600">{userProfile.games_won}</div>
                      <div className="text-gray-500 text-sm">فوز</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <div className="text-3xl font-bold text-blue-600">
                        {userProfile.games_played > 0 
                          ? Math.round((userProfile.games_won / userProfile.games_played) * 100) 
                          : 0}%
                      </div>
                      <div className="text-gray-500 text-sm">نسبة الفوز</div>
                    </div>
                  </div>
                </div>

                {/* آخر المحاولات */}
                {userProfile.recent_attempts?.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-xl">
                    <h3 className="font-bold text-primary mb-4">آخر المحاولات</h3>
                    <div className="space-y-3">
                      {userProfile.recent_attempts.map((attempt, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-primary">{attempt.scenario_title}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(attempt.created_at).toLocaleDateString('ar-SA')}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-bold text-gold">{attempt.score} / {attempt.max_score}</span>
                            {attempt.passed ? (
                              <CheckCircle className="w-6 h-6 text-green-500" />
                            ) : (
                              <X className="w-6 h-6 text-red-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl p-12 shadow-xl text-center">
                <UserCircle className="w-24 h-24 text-gray-300 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-primary mb-4">سجل الدخول لعرض ملفك</h3>
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark"
                >
                  تسجيل الدخول
                </button>
              </div>
            )}
          </div>
        )}

        {/* قاعة المحكمة */}
        {activeTab === 'courtroom' && (
          <div className="space-y-6">
            {!sessionActive ? (
              <>
                {/* اختيار الدور والقضية */}
                <div className="bg-white rounded-2xl p-6 shadow-xl">
                  <h3 className="text-xl font-heading font-bold text-primary mb-6 flex items-center gap-2">
                    <UserCircle className="w-6 h-6 text-gold" />
                    اختر دورك في المحاكمة
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {roles.map(role => (
                      <button
                        key={role.id}
                        onClick={() => setSelectedRole(role.id)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          selectedRole === role.id
                            ? 'border-gold bg-gold/10'
                            : 'border-gray-200 hover:border-gold/50'
                        }`}
                      >
                        <div className={`w-12 h-12 ${role.color} rounded-full flex items-center justify-center mx-auto mb-3`}>
                          <role.icon className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="font-bold text-primary">{role.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* القضايا */}
                <div className="bg-white rounded-2xl p-6 shadow-xl">
                  <h3 className="text-xl font-heading font-bold text-primary mb-6">اختر القضية</h3>
                  <div className="space-y-4">
                    {trainingCases.map(caseItem => (
                      <div
                        key={caseItem.id}
                        onClick={() => setSelectedCase(caseItem)}
                        className={`p-4 border-2 rounded-xl cursor-pointer ${
                          selectedCase?.id === caseItem.id ? 'border-gold bg-gold/5' : 'border-gray-200'
                        }`}
                      >
                        <h4 className="font-bold text-primary">{caseItem.title}</h4>
                        <p className="text-gray-600 text-sm">{caseItem.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* زر البدء */}
                <div className="text-center">
                  <button
                    onClick={startSession}
                    disabled={!selectedRole || !selectedCase}
                    className="px-12 py-4 bg-gold text-white text-xl font-bold rounded-xl hover:bg-gold-dark disabled:opacity-50"
                  >
                    <Play className="w-6 h-6 inline ml-2" />
                    بدء جلسة المحاكمة
                  </button>
                </div>
              </>
            ) : (
              /* جلسة المحاكمة النشطة */
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl overflow-hidden">
                  {/* مراحل المحاكمة */}
                  <div className="bg-gray-50 p-4 border-b">
                    <div className="flex items-center gap-2 overflow-x-auto">
                      {phases.map((phase) => (
                        <button
                          key={phase.id}
                          onClick={() => setCurrentPhase(phase.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                            currentPhase === phase.id ? 'bg-gold text-white' : 'bg-white text-gray-600'
                          }`}
                        >
                          <phase.icon className="w-4 h-4" />
                          <span className="text-sm">{phase.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* الرسائل */}
                  <div className="h-96 overflow-y-auto p-4 space-y-4">
                    {messages.map(msg => {
                      const roleInfo = roles.find(r => r.id === msg.role);
                      return (
                        <div key={msg.id} className={`flex gap-3 ${msg.role === 'system' ? 'justify-center' : ''}`}>
                          {msg.role !== 'system' && (
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleInfo?.color || 'bg-gray-400'}`}>
                              {roleInfo?.icon ? <roleInfo.icon className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                            </div>
                          )}
                          <div className={`flex-1 ${msg.role === 'system' ? 'text-center' : ''}`}>
                            {msg.role === 'system' ? (
                              <div className="inline-block bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm">
                                {msg.text}
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-bold text-primary">{msg.roleName}</span>
                                  <span className="text-xs text-gray-400">{msg.time}</span>
                                </div>
                                <div className="bg-gray-100 rounded-lg p-3">
                                  <p className="text-gray-700">{msg.text}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* إدخال الرسالة */}
                  <div className="p-4 border-t bg-gray-50">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="اكتب مرافعتك..."
                        className="flex-1 px-4 py-3 border border-gray-200 rounded-lg"
                      />
                      <button
                        onClick={sendMessage}
                        className="px-6 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* اللوحة الجانبية */}
                <div className="space-y-4">
                  {selectedCase && (
                    <div className="bg-white rounded-xl p-4 shadow-lg">
                      <h4 className="font-bold text-primary mb-3">القضية الحالية</h4>
                      <p className="font-medium">{selectedCase.title}</p>
                      <p className="text-sm text-gray-500 mt-2">{selectedCase.description}</p>
                    </div>
                  )}

                  {/* عبارات جاهزة */}
                  {selectedRole && courtPhrases[selectedRole] && (
                    <div className="bg-white rounded-xl p-4 shadow-lg">
                      <h4 className="font-bold text-primary mb-3">عبارات جاهزة</h4>
                      <div className="space-y-2">
                        {courtPhrases[selectedRole].map((phrase, idx) => (
                          <button
                            key={idx}
                            onClick={() => setNewMessage(phrase)}
                            className="w-full text-right px-3 py-2 bg-gray-50 hover:bg-gold/10 rounded-lg text-sm"
                          >
                            {phrase}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setSessionActive(false);
                      setMessages([]);
                    }}
                    className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    <Pause className="w-5 h-5 inline ml-2" />
                    إنهاء الجلسة
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-black/20 border-t border-white/10 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/60">
            المحكمة الافتراضية الأهلية - منصة تدريبية لمكتب المحامي هشام يوسف الخياط
          </p>
        </div>
      </footer>
    </div>
  );
}
